import ScorInvoicesPage from './ScorInvoicesPage';
import { parsePhoneNumber } from 'react-phone-number-input';
import { ScorReportSections, ScorReportFiles, ScorReportHistory, ScorPagination, ScorCopy } from './ScorReportExtras';
import useAppSelector from 'hooks/shared/store/useAppSelector';
import { authSelector } from 'selectors/modules/auth';
import { AuthStatus } from 'slices/modules/auth/authState';
import { readSubscriptionConfirmation, saveSubscriptionConfirmation, clearSubscriptionConfirmation } from './scorSubscriptionConfirmation';
import { useTranslation } from 'react-i18next';
/* eslint-disable @typescript-eslint/no-misused-promises, @typescript-eslint/promise-function-async */
import { scorT, scorCount, getScorLocale, scorUiText, scorMetadataText } from 'i18n/scorLocale';
import {
  Button,
  Container,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  useToast,
} from '@chakra-ui/react';
import { ChatRoomType } from 'enums/modules/chat/chatRoomType';
import useAppDispatch from 'hooks/shared/store/useAppDispatch';
import useToastError from 'hooks/shared/useToastError';
import PageLayout from 'presentation/layout/shared/page/PageLayout';
import {
  DragEvent,
  FC,
  FormEvent,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  FiAlertCircle,
  FiArrowRight,
  FiBarChart2,
  FiCheck,
  FiCopy,
  FiDownload,
  FiExternalLink,
  FiFileText,
  FiLink,
  FiMessageCircle,
  FiSearch,
  FiSend,
  FiShield,
  FiTrendingUp,
} from 'react-icons/fi';
import { toCanvas } from 'qrcode';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  scorApi,
  useApplyScorReferralMutation,
  useCreateScorPaymentMutation,
  useCreateScorRequestMutation,
  useCreateScorShareMutation,
  useCreateScorSubscriptionMutation,
  useFetchScorCategoriesQuery,
  useFetchScorDocumentsQuery,
  useFetchScorFaqQuery,
  useMarkScorFaqViewedMutation,
  useFetchScorInvoiceQuery,
  useFetchScorMyOrdersQuery,
  useFetchScorMyInvoicesQuery,
  useFetchScorMyPurchasesQuery,
  useFetchScorMyReportsQuery,
  useFetchScorShareReportsQuery,
  useFetchScorPoolDetailQuery,
  useFetchScorPoolQuery,
  useFetchScorPricingQuery,
  useFetchScorReferralQuery,
  useFetchScorReportDetailQuery,
  useFetchScorSharesQuery,
  useFetchScorSharedWithMeQuery,
  useFetchScorShowcaseQuery,
  useFetchScorSubscriptionQuery,
  useFetchSharedScorReportQuery,
  useFindScorByCodeQuery,
  useFetchScorSharedPreviewQuery,
  useOpenScorOrderChatMutation,
  useRevokeScorShareMutation,
  useSaveScorPayerMutation,
  useUploadScorOrderDocumentsMutation,
} from 'rtkQuery/sharespro/queries/scor';
import { useFetchChatListQuery } from 'rtkQuery/sharespro/queries/chat';
import { useFetchProfileQuery } from 'rtkQuery/sharespro/queries/profile';
import { isSharesproFetchQueryError } from 'rtkQuery/sharespro/isSharesproFetchQueryError';
import { SharesproApiResponse } from 'rtkQuery/sharespro/types/sharesproApiResponse';
import AppRoute from 'router/AppRoute';
import { widgetChatSlice } from 'slices/modules/chat/widgetChatSlice';
import documentsImage from 'assets/img/scor/documents-vault-3d.webp';
import orderImage from 'assets/img/scor/order-review-3d.webp';
import referralImage from 'assets/img/scor/referral-circle-3d.webp';
import analyticsImage from 'assets/img/scor/scor-analytics-3d.webp';
import subscriptionImage from 'assets/img/scor/subscription-access-3d.webp';
import {
  formatScorInvoiceMoney,
  isScorInvoiceFinalStatus,
} from './scorInvoice';
import styles from './scor.module.scss';
import { normalizeScorLineBreaks } from './scorText';
import { formatScorFaqAnswer } from './scorFaq';
import { hasScorUploadRules, validateScorFiles, normalizeScorOrders, hasScorOutcome } from './scorContract';
import { formatScorOutcomeLabel } from './scorOverview';
import { formatScorDaysLeft, formatScorIncludedReports, formatScorOutcomeReports, formatScorInvitations } from './scorPlural';
import {
  formatScorWorkingDays,
  SCOR_COMMENT_MAX_LENGTH,
  SCOR_ISSUER_MAX_LENGTH,
  ScorOrderValidationValues,
  validateScorOrder,
} from './scorOrder';

const formatDate = (value?: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat(getScorLocale(), { dateStyle: 'medium' }).format(date);
};

const SidebarActiveUntil: FC<{ value?: string | null }> = ({ value }) => {
  const date = value ? new Date(value) : undefined;
  if (getScorLocale() !== 'ru-RU' || !date || Number.isNaN(date.getTime())) {
    return <>{scorT('activeUntil', { v0: formatDate(value) })}</>;
  }

  const parts = new Intl.DateTimeFormat(getScorLocale(), { dateStyle: 'medium' }).formatToParts(date);
  const yearIndex = parts.findIndex((part) => part.type === 'year');
  const dayAndMonth = parts.slice(0, yearIndex).map((part) => part.value).join('').trim();
  const year = parts.slice(yearIndex).map((part) => part.value).join('').replace(/\s+/g, '\u00a0');

  return <>{scorT('activeUntil', { v0: dayAndMonth })}{' '}<span className={styles.subscriptionYear}>{year}</span></>;
};

const formatMoney = (value?: number | null): string =>
  typeof value === 'number'
    ? new Intl.NumberFormat(getScorLocale(), {
        style: 'currency',
        currency: 'RUB',
        maximumFractionDigits: 0,
      }).format(value)
    : scorT('onRequest');

const getInvoiceStatusClassName = (status?: number): string => {
  if (status === 2) return styles.invoiceStatusPaid;
  if (status === 3) return styles.invoiceStatusWarning;
  if (status === 8 || status === 9) return styles.invoiceStatusFinal;

  return '';
};

const getNumberParam = (value?: string): number => Number(value ?? 0);

const getLastPathSegment = (pathname: string): string => {
  const segments = pathname.split('/').filter(Boolean);

  return decodeURIComponent(segments[segments.length - 1] ?? '');
};

const getScoreClassName = (score?: number | null): string => {
  if (typeof score !== 'number') return '';
  if (score < 5) return styles.poolScoreRisk;
  if (score < 7) return styles.poolScoreWarning;

  return styles.poolScorePositive;
};

const getScorFieldErrors = (error: unknown): Record<string, string> => {
  if (!isSharesproFetchQueryError(error)) return {};
  const response = error.data as SharesproApiResponse<unknown>;

  return Object.fromEntries(
    (response.field_errors ?? []).map((item) => [/^(documents|files|file|document)(\[\]|\.\d+)?$/.test(item.field) ? 'documents' : item.field, item.error])
  );
};

const ReportCover: FC<{
  report: ApiScorMyReportItem | ApiScorPoolReportItem;
  className: string;
}> = ({ report, className }) => (
  report.cover_url
    ? <img className={className} src={report.cover_url} alt="" />
    : (
      <span
        className={`${className} ${styles.coverFallback}`}
        style={{ backgroundColor: report.cover_color ?? '#1B5E4A' }}
        aria-hidden="true"
      >
        {(report.issuer ?? 'S').trim().charAt(0).toLocaleUpperCase(getScorLocale())}
      </span>
      )
);

const hasLegalDocumentContent = (item: ApiScorLegalDocument): boolean =>
  Boolean(item.body) || Boolean(item.url);

const LegalDocumentDialog: FC<{
  item?: ApiScorLegalDocument;
  onClose: () => void;
}> = ({ item, onClose }) => (
  <Modal isOpen={Boolean(item)} onClose={onClose} size="4xl" scrollBehavior="inside">
    <ModalOverlay />
    <ModalContent className={styles.legalDocumentModal}>
      <ModalHeader>{scorUiText(item?.title, 'scorDocument')}</ModalHeader>
      <ModalCloseButton />
      <ModalBody>
        {item?.body && <div className={styles.legalDocumentBody}>{item.body}</div>}
        {!item?.body && item?.url && <iframe className={styles.legalDocumentFrame} src={item.url} title={scorUiText(item.title, 'scorDocument')} />}
        {item?.url && <Button as="a" href={item.url} target="_blank" rel="noreferrer" mt="16px" mb="16px" variant="outline">{scorT('openInANewTab')}</Button>}
      </ModalBody>
    </ModalContent>
  </Modal>
);

const PricingDocument: FC<{ document?: ApiScorLegalDocument | null }> = ({ document }) => {
  const [open, setOpen] = useState(false);
  if (!document) return null;

  return <div className={styles.orderLegalLinks}>
    <button type="button" disabled={!hasLegalDocumentContent(document)} onClick={() => setOpen(true)}>{document.title}</button>
    <LegalDocumentDialog item={open ? document : undefined} onClose={() => setOpen(false)} />
  </div>;
};

const getScorErrorResponse = (error: unknown): SharesproApiResponse<unknown> | undefined =>
  isSharesproFetchQueryError(error) ? error.data as SharesproApiResponse<unknown> : undefined;

const getPendingInvoice = (
  subscription?: ApiScorSubscriptionResponseData | null,
  showcase?: ApiScorShowcaseResponseData | null
): ApiScorInvoice | undefined => {
  const invoice = subscription?.pending_invoice !== undefined ? subscription.pending_invoice : showcase?.pending_invoice;

  return invoice?.id && invoice.can_pay !== false && !isScorInvoiceFinalStatus(invoice.status) ? invoice : undefined;
};

const PendingInvoice: FC<{ invoice: ApiScorInvoice }> = ({ invoice }) => (
  <div className={styles.stack}>
    <strong>{scorT('pendingPayment')}</strong>
    <span>{formatScorInvoiceMoney(invoice.amount_minor)}</span>
    <Button as={Link} to={AppRoute.scorInvoice(invoice.id!)} colorScheme="green">{scorT('payNow')}</Button>
  </div>
);

// Older API versions omit this flag and continue to sell subscriptions.
const isSubscriptionSaleEnabled = (...sources: Array<{ subscription_enabled?: boolean; pricing?: ApiScorPricing } | null | undefined>): boolean =>
  !sources.some((source) => source?.subscription_enabled === false || source?.pricing?.subscription_enabled === false);

const useSubscriptionSaleEnabled = (): boolean => {
  const subscription = useFetchScorSubscriptionQuery();
  const showcase = useFetchScorShowcaseQuery();

  return isSubscriptionSaleEnabled(subscription.data?.data, showcase.data?.data);
};

const SubscriptionAction: FC = () => {
  const subscriptionQuery = useFetchScorSubscriptionQuery();
  const showcaseQuery = useFetchScorShowcaseQuery();
  const subscription = subscriptionQuery.data?.data;
  const showcase = showcaseQuery.data?.data;
  if (!isSubscriptionSaleEnabled(subscription, showcase) || subscriptionQuery.isLoading || subscriptionQuery.isFetching || subscriptionQuery.isError) return null;
  const pendingInvoice = getPendingInvoice(subscription, showcase);
  if (pendingInvoice) return <PendingInvoice invoice={pendingInvoice} />;
  if (subscription?.subscription?.is_active) return null;
  if (subscription?.has_pending_request === true || showcase?.has_pending_request === true) {
    return <div role="status"><b>{scorT('requestSubmitted')}</b><p>{scorT('aManagerWillContactYou')}</p></div>;
  }
  if (subscriptionQuery.isFetching || subscriptionQuery.isError || subscription?.has_pending_request !== false) return null;

  return <Button as={Link} to={AppRoute.scorSubscription}>{scorT('subscribe')}</Button>;
};

const ScorError: FC<{ error: unknown }> = ({ error }) => {
  const response = getScorErrorResponse(error);
  const waiting = response?.error_code === 'REPORT_NOT_READY';

  return <div className={styles.empty} role={waiting ? 'status' : 'alert'}>
    <p>{response?.error_message ?? (waiting ? '' : scorT('unableToLoadDataTryRefreshingThe'))}</p>
    {response?.error_code === 'SUBSCRIPTION_REQUIRED' && <SubscriptionAction />}
  </div>;
};

const useScorToastError = (): ((error: unknown) => void) => {
  const toast = useToast();
  const fallback = useToastError();

  return (error) => {
    const response = getScorErrorResponse(error);
    const message = Object.values(getScorFieldErrors(error)).filter(Boolean).join('. ') || response?.error_message;
    if (message) toast({ title: message, status: 'error', position: 'top-right' });
    else fallback(error);
  };
};

const SectionHeader: FC<{
  eyebrow?: string;
  title: string;
  description?: string;
  action?: ReactNode;
}> = ({ eyebrow = 'SharesPro SCOR', title, description, action }) => (
  <header className={styles.pageHeader}>
    <div>
      <div className={styles.eyebrow}>{eyebrow}</div>
      <h1 className={styles.pageTitle}>{title}</h1>
      {description && <div className={styles.muted}>{description}</div>}
    </div>
    {action}
  </header>
);

const QueryState: FC<{
  isLoading?: boolean;
  isError?: boolean;
  isEmpty?: boolean;
  error?: unknown;
  emptyText?: string;
  children: ReactNode;
}> = ({ isLoading, isError, isEmpty, error, emptyText, children }) => {
  if (isLoading) {
    return <div className={styles.empty}><Spinner color="primary.600" /></div>;
  }
  if (isError) {
    return <ScorError error={error} />;
  }
  if (isEmpty) {
    return <div className={styles.empty}>{emptyText ?? scorT('nothingHereYet')}</div>;
  }

  return <>{children}</>;
};

const ReportRow: FC<{
  report: ApiScorMyReportItem | ApiScorPoolReportItem;
  href: string;
  actionLabel?: 'reportDetails' | 'open';
}> = ({ report, href, actionLabel = 'reportDetails' }) => (
  <div className={styles.reportRow}>
    <div className={styles.reportIdentity}>
      <ReportCover className={styles.cover} report={report} />
      <div className={styles.reportMeta}>
        <div className={styles.reportName}>{report.issuer ?? scorT('untitled')}</div>
        <div className={styles.muted}>
          {[scorMetadataText(report.category), report.public_code].filter(Boolean).join(' · ') || scorT('scorReport')}
        </div>
      </div>
    </div>
    <span className={`${styles.score} ${getScoreClassName(report.score)}`} aria-label={typeof report.score === 'number' ? scorT('score', { v0: report.score, v1: report.score < 5 ? scorT('highRisk') : report.score < 7 ? scorT('needsAttention') : scorT('positiveAssessment') }) : scorT('noScoreAvailable')}>{report.score ?? '—'}</span>
    <Button as={Link} to={href} variant="outline" rightIcon={<FiArrowRight />}>{scorT(actionLabel)}</Button>
  </div>
);

const ScorUiIcon: FC<{ item?: ApiScorUiItem }> = ({ item }) => {
  const [failedUrl, setFailedUrl] = useState<string>();
  const icons: Record<string, typeof FiFileText> = {
    file: FiFileText,
file_text: FiFileText,
'file-text': FiFileText,
    chart: FiBarChart2,
'bar-chart-2': FiBarChart2,
    send: FiSend,
link: FiLink,
shield: FiShield,
search: FiSearch,
  };
  const Icon = item?.icon ? icons[item.icon] : undefined;
  if (item?.icon_url && failedUrl !== item.icon_url) return <img className={styles.serverIcon} src={item.icon_url} alt="" onError={() => setFailedUrl(item.icon_url ?? undefined)} />;
  if (Icon) return <Icon aria-hidden="true" className={styles.serverIcon} />;

  return null;
};

const ScorSidebar: FC = () => {
  const location = useLocation();
  const { data, fulfilledTimeStamp, isLoading, isFetching, isError, error, refetch } = useFetchScorSubscriptionQuery(undefined, { refetchOnMountOrArgChange: true });
  const showcaseQuery = useFetchScorShowcaseQuery();
  const sharedQuery = useFetchScorSharedWithMeQuery();
  const subscription = data?.data?.subscription;
  const showcase = showcaseQuery.data?.data;
  const saleEnabled = isSubscriptionSaleEnabled(data?.data, showcase);
  const showSubscription = saleEnabled || subscription?.is_active === true;
  const saved = readSubscriptionConfirmation();
  const pendingInvoice = getPendingInvoice(data?.data, showcase);
  const hasPendingRequest = data?.data?.has_pending_request ?? (showcase?.has_pending_request === true || Boolean(saved && (fulfilledTimeStamp ?? 0) <= saved.savedAt));
  const ui = { ...showcase?.ui, ...data?.data?.ui };
  const referralSource = data?.data ?? showcase;
  const referralEnabled = referralSource?.referral_enabled === true && Boolean(referralSource.ui?.scor_referral);
  const reportsUsed = subscription?.reports_used ?? 0;
  const reportsLimit = subscription?.reports_limit ?? 0;
  const progress = reportsLimit > 0
    ? Math.min(100, Math.round((reportsUsed / reportsLimit) * 100))
    : 0;
  const items = [
    [AppRoute.scor, scorT('overview'), undefined, false],
    [AppRoute.scorOrder, scorT('requestAReport'), undefined, true],
    [AppRoute.scorReports, scorT('myScorReports'), showcase?.my_reports?.length, false],
    [AppRoute.scorPool, scorT('reportLibrary'), showcase?.pool?.total, false],
    [AppRoute.scorFind, scorT('findAReportByCode'), undefined, false],
    [AppRoute.scorShared, scorT('sharedWithMe'), sharedQuery.data?.data?.items?.length, false],
    [AppRoute.scorInvites, scorT('myInvitations'), undefined, true],
    ...(referralEnabled ? [[AppRoute.scorReferral, scorT('inviteAFriend'), undefined, false] as const] : []),
    ...(showSubscription ? [[AppRoute.scorSubscription, scorT(saleEnabled ? 'subscriptionAndPricing' : 'scorSubscription'), undefined, false] as const] : []),
    [AppRoute.scorInvoices, scorT('invoicesTitle'), undefined, true],
    [AppRoute.scorFaq, scorT('questionsAndAnswers'), undefined, false],
    [AppRoute.scorDocuments, scorT('documents'), undefined, false],
  ] as const;

  const uiByRoute: Record<string, ApiScorUiItem | undefined> = {
    [AppRoute.scorOrder]: ui.scor_order_new,
[AppRoute.scorReports]: ui.scor_my_reports,
    [AppRoute.scorPool]: ui.scor_base,
[AppRoute.scorShared]: ui.scor_shared,
    [AppRoute.scorInvites]: ui.scor_invites,
[AppRoute.scorReferral]: ui.scor_referral,
    [AppRoute.scorSubscription]: ui.scor_subscription,
[AppRoute.scorFaq]: ui.showcase_faq,
    [AppRoute.scorDocuments]: ui.scor_documents,
  };

  return (
    <aside className={styles.sidebar}>
      {showSubscription && <div className={styles.subscriptionCard}>
        <div className={styles.eyebrow}>{scorT('scorSubscription')}</div>
        <QueryState isLoading={isLoading || isFetching || (!isError && !data?.data)} isError={isError} error={error}>
          <div className={styles.subscriptionTitle}>
            {subscription?.is_active
              ? <SidebarActiveUntil value={subscription.paid_until} />
              : !pendingInvoice && hasPendingRequest ? scorT('requestSubmitted') : scorT('fullAccessToScor')}
            {!subscription?.is_active && !pendingInvoice && hasPendingRequest && <p className={styles.muted}>{scorT('aManagerWillContactYou')}</p>}
          </div>
          <div className={styles.subscriptionPrice}>
            {subscription?.price_month == null ? '—' : formatMoney(subscription.price_month)}{' '}{scorT('perMonth')}</div>
          <div className={styles.subscriptionProgress}>
            <i style={{ width: `${progress}%` }} />
          </div>
          <div className={styles.subscriptionStats}>
            <span>{scorT('reportsThisPeriod')}<b>{reportsUsed}{' '}{scorT('of33')}{' '}{reportsLimit || '—'}</b></span>
            {subscription?.is_active && <span>{scorT('remaining')}<b>{formatScorDaysLeft(subscription.days_left)}</b></span>}
          </div>
        </QueryState>
        <img src={subscriptionImage} alt="" />
      </div>}
      <nav className={styles.sidebarNav} aria-label={scorT('scorNavigation')}>
        {items.map(([href, label, count, separatorAfter]) => {
          const isRoot = href === AppRoute.scor;
          const active = isRoot
            ? location.pathname === AppRoute.scor
            : location.pathname.startsWith(href);

          return (
            <div key={href}>
              <Link
                to={href}
                className={`${styles.navItem} ${active ? styles.navItemActive : ''}`}
                aria-current={active ? 'page' : undefined}
              >
                <ScorUiIcon item={uiByRoute[href]} />{label}
                {typeof count !== 'undefined' && <span className={styles.navCount}>{count}</span>}
              </Link>
              {separatorAfter && <div className={styles.navSeparator} />}
            </div>
          );
        })}
      </nav>
      {referralSource && !isLoading && !isFetching && typeof referralSource.referral_enabled !== 'boolean' && <div className={styles.card} role="alert">
        <p>{scorT('referralSettingsUnavailable')}</p>
        <Button size="sm" variant="outline" onClick={() => { void refetch(); }}>{scorT('reloadReferralSettings')}</Button>
      </div>}
      {(showcase?.pool?.with_outcome ?? 0) > 0 && <div className={styles.accuracyCard}>
        <div className={styles.reportName}>{scorT('recordedOutcomes')}</div>
        <div className={styles.accuracyValue}>{showcase?.pool?.with_outcome ?? '—'}</div>
        <div className={styles.muted}>
          {formatScorOutcomeReports(showcase?.pool?.with_outcome)}{scorT('weAlsoShowDiscrepancies')}</div>
      </div>}
    </aside>
  );
};

const OrderActions: FC<{ order: ApiScorOrder; showFollowUp?: boolean }> = ({ order, showFollowUp = true }) => {
  const dispatch = useAppDispatch();
  const toastError = useScorToastError();
  const [openChat, { isLoading: isChatLoading }] = useOpenScorOrderChatMutation();

  const handleChat = async (): Promise<void> => {
    if (!order.id) return;
    try {
      if (order.chat_id) {
        dispatch(widgetChatSlice.actions.open({ id: order.chat_id, type: ChatRoomType.Private, name: `SCOR · ${order.issuer ?? scorT('request')}` }));

        return;
      }
      const response = await openChat({ order_id: order.id }).unwrap();
      if (response.data?.chat_id) {
        dispatch(widgetChatSlice.actions.open({
          id: response.data.chat_id,
          type: ChatRoomType.Private,
          name: response.data.name ?? `SCOR · ${order.issuer ?? scorT('request')}`,
        }));
      }
    } catch (error) {
      toastError(error);
    }
  };

  if (!order.id) return null;

  return (
    <div className={styles.buttonRow}>
      {order.report_id && <Button as={Link} to={AppRoute.scorReport(order.report_id)} size="sm">{scorT('openReport')}</Button>}
      <Button size="sm" variant="outline" leftIcon={<FiMessageCircle />} isLoading={isChatLoading} onClick={() => { void handleChat(); }}>{scorT('chat')}</Button>
      {showFollowUp && order.id && order.needs_action && <OrderFollowUp order={order} />}
    </div>
  );
};

const OrderFollowUp: FC<{ order: ApiScorOrder }> = ({ order }) => {
  const rulesQuery = useFetchScorMyOrdersQuery();
  const rules = rulesQuery.data?.data?.upload;
  const [fileError, setFileError] = useState('');
  const toast = useToast();
  const toastError = useScorToastError();
  const [upload] = useUploadScorOrderDocumentsMutation();
  const [comment, setComment] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [isSending, setIsSending] = useState(false);
  const sendingRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (sendingRef.current || !order.id) return;
    const error = validateScorFiles(files, rules);
    if (error) {
 setFileError(error);

 return;
}
    sendingRef.current = true;
    setIsSending(true);
    try {
      const response = await upload({ order_id: order.id, comment: comment.trim() || undefined, documents: files, uploadField: rules?.field }).unwrap();
      setComment('');
      setFiles([]);
      setFileError('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      toast({
        title: response.data?.message ?? scorT('informationSentToTheAnalyst'),
        status: 'success',
        position: 'top-right',
      });
    } catch (error) {
      const errors = getScorFieldErrors(error);
      setFileError(errors.documents ?? errors.comment ?? '');
      if (!Object.keys(errors).length) toastError(error);
    } finally {
      sendingRef.current = false;
      setIsSending(false);
    }
  };

  return (
    <form className={styles.orderFollowUp} aria-label={scorT('additionalInformation')} aria-invalid={Boolean(fileError)} onSubmit={(event) => { void submit(event); }}>
      {order.needs_action && order.need_info_comment && <p className={styles.multilineText}>{normalizeScorLineBreaks(order.need_info_comment)}</p>}
      <input className={styles.nativeInput} aria-label={scorT('commentForTheAnalyst')} disabled={isSending} value={comment} onChange={(event) => setComment(event.target.value)} placeholder={scorT('commentForTheAnalyst')} />
      {files.length > 0 && <ul className={styles.followUpFiles}>{files.map((file, index) => (
        <li key={index}><span>{file.name}</span><button type="button" disabled={isSending} aria-label={scorT('remove', { v0: file.name })} onClick={() => { const next = files.filter((_, itemIndex) => itemIndex !== index); setFiles(next); setFileError(validateScorFiles(next, rules) ?? ''); }}>×</button></li>
      ))}</ul>}
      {rules?.hint && <small>{rules.hint}</small>}
      {fileError && <small role="alert" className={styles.fieldError}>{fileError}</small>}
      {!hasScorUploadRules(rules) && <div role="alert">{scorT('unableToLoadUploadSettings')}{' '}<button type="button" disabled={isSending} onClick={() => { void rulesQuery.refetch(); }}>{scorT('reload')}</button></div>}
      <div className={styles.followUpButtons}>
        <Button type="button" size="sm" variant="outline" isDisabled={isSending} onClick={() => fileInputRef.current?.click()}>{scorT('addFiles')}</Button>
        <Button type="submit" size="sm" colorScheme="green" isLoading={isSending} isDisabled={!order.id || files.length === 0 || !hasScorUploadRules(rules)}>{scorT('send')}</Button>
      </div>
      <input ref={fileInputRef} aria-label={scorT('filesForTheAnalyst')} hidden multiple accept={rules?.accept} disabled={isSending || !hasScorUploadRules(rules)} type="file" onChange={(event) => {
        const selected = Array.from(event.target.files ?? []);
        if (!sendingRef.current) {
          const next = [...files, ...selected];
          const error = validateScorFiles(next, rules);
          setFileError(error ?? '');
          if (!error) setFiles(next);
        }
        event.target.value = '';
      }} />
    </form>
  );
};

const Overview: FC = () => {
  const { data, isLoading, isError, error } = useFetchScorShowcaseQuery();
  const orders = useFetchScorMyOrdersQuery();
  const showcase = data?.data;
  const reports = showcase?.my_reports ?? [];
  const activeOrders = normalizeScorOrders(orders.data?.data?.items ?? showcase?.active_orders ?? []);
  const whileWaiting = orders.data?.data?.while_waiting ?? [];
  const primaryOrder = activeOrders[0];

  return (
    <div className={styles.stack}>
      <div>
        <div className={styles.screenKicker}>{scorT('scorInvestmentAnalysis')}</div>
          <section className={styles.hero}>
          <div className={styles.heroCopy}>
          <div className={styles.heroTag}><FiShield />{' '}{scorT('investmentAnalysis')}</div>
          <h1 className={styles.heroTitle}>{scorT('decisionsStartWithDocumentsNotPromises')}</h1>
          <p className={styles.heroText}>{scorT('scorBringsTheTermsOfAnOffer')}</p>
          <div className={styles.buttonRow}>
              <Button as={Link} to={AppRoute.scorOrder} colorScheme="green"><ScorUiIcon item={showcase?.ui?.scor_order_new} />{scorT('requestAReport')}</Button>
              <Button as={Link} to={AppRoute.scorReports} variant="ghost" rightIcon={<FiArrowRight />}><ScorUiIcon item={showcase?.ui?.scor_my_reports} />{scorT('myScorReports')}</Button>
            </div>
          </div>
          <div className={styles.heroVisual}>
            <img className={styles.heroImage} src={analyticsImage} alt={scorT('scorAnalyticalReport')} />
            <div className={`${styles.visualChip} ${styles.chipScore}`}>
              <span>{scorT('reportStatus')}</span>
              <b>{primaryOrder?.status_label ?? scorT('readyToStart')}</b>
              <em>{primaryOrder?.needs_action ? scorT('documentsRequired') : scorT('reviewScopeDefined')}</em>
            </div>
            <div className={`${styles.visualChip} ${styles.chipRisk}`}>
              <span>{scorT('reviewScope')}</span>
              <b>{scorT('5Factors')}</b>
              <em>{scorT('economicsAndRisks')}</em>
            </div>
            <div className={styles.dossierStamp}>
              <span>{scorT('scorAnalysis')}</span>
              <b>{scorT('independentAssessment')}</b>
            </div>
          </div>
          </section>

        <div className={styles.instrumentRibbon} aria-label={scorT('areasOfReview')}>
          <div className={styles.instrumentItem}><span className={styles.instrumentIcon}><FiFileText /></span><b>{scorT('documents')}</b><small>{scorT('termsAndAnnexes')}</small></div>
          <div className={styles.instrumentItem}><span className={styles.instrumentIcon}><FiBarChart2 /></span><b>{scorT('dealEconomics')}</b><small>{scorT('feesAndScenarios')}</small></div>
          <div className={styles.instrumentItem}><span className={styles.instrumentIcon}><FiTrendingUp /></span><b>{scorT('liquidity')}</b><small>{scorT('exitOptionsAndRestrictions')}</small></div>
          <div className={styles.instrumentItem}><span className={styles.instrumentIcon}><FiShield /></span><b>{scorT('riskAssessment')}</b><small>{scorT('independentScor')}</small></div>
        </div>

        <div className={styles.editorialIndex}>
          <div><span>01</span><b>{scorT('termsReview')}</b><small>{scorT('feesAndDealStructure')}</small></div>
          <div><span>02</span><b>{scorT('exitScenarios')}</b><small>{scorT('liquidityAndRestrictions')}</small></div>
          <div><span>03</span><b>{scorT('independentScore')}</b><small>{scorT('110Methodology')}</small></div>
        </div>
      </div>

      <div className={styles.dossierLayout}>
        <section className={styles.dossierList}>
          <div className={styles.cardHeader}>
            <div><h2 className={styles.sectionTitle}>{scorT('yourReports')}</h2><div className={styles.muted}>{scorT('ongoingAndCompletedReviews')}</div></div>
            <Button as={Link} to={AppRoute.scorReports} variant="ghost">{scorT('archive')}</Button>
          </div>
          {(isError || orders.isError) && <ScorError error={error ?? orders.error} />}
          {(isLoading || orders.isLoading) && <div className={styles.empty}><Spinner color="primary.600" /></div>}
          {!isLoading && !orders.isLoading && activeOrders.length === 0 && reports.length === 0 && (
            <div className={styles.empty}>{scorT('yourOngoingAndCompletedReportsWillAppear')}</div>
          )}
          {activeOrders.slice(0, 2).map((order) => (
            <div className={styles.dossierRow} key={order.request_id != null ? `request-${order.request_id}` : `order-${order.id}`}>
              <span className={styles.folio}>SP–{order.id ?? '—'}</span>
              <div><b>{order.issuer ?? order.service ?? scorT('scorReport83')}</b><small>{order.status_label ?? (order.id ? scorT('inProgress') : scorT('underReview'))} · {formatDate(order.created_at)}</small></div>
              <div className={styles.dossierRowActions}>
                <em>{order.report_id ? scorT('ready') : order.needs_action && order.id ? scorT('informationNeeded') : order.id ? scorT('inProgress') : scorT('underReview')}</em>
                <OrderActions order={order} showFollowUp={false} />
              </div>
              {order.id && order.needs_action && <OrderFollowUp order={order} />}
            </div>
          ))}
          {reports.slice(0, Math.max(1, 3 - activeOrders.length)).map((report) => (
            <Link className={styles.dossierRow} key={`report-${report.id}`} to={AppRoute.scorReport(report.id ?? 0)}>
              <span className={styles.folio}>{report.public_code ?? `SP–${report.id ?? '—'}`}</span>
              <div><b>{report.issuer ?? scorT('scorReport83')}</b><small>{scorMetadataText(report.category) ?? scorT('completedReport')} · {formatDate(report.published_at)}</small></div>
              <em>{report.score ?? '—'}</em>
            </Link>
          ))}
        </section>

        <aside className={styles.analystMemo}>
          <span className={styles.memoLabel}>{scorT('analystSNote')}</span>
          <h3>{scorT('theMainRiskOftenLiesInEarly')}</h3>
          <p>{scorT('weComparePromisesWithDocumentsAndReview')}</p>
          <Link className={styles.memoLink} to={AppRoute.scorPool}>{scorT('exploreSimilarCases')}</Link>
          <div className={styles.memoStat}><b>{showcase?.pool?.with_outcome ?? '—'}</b><span>{formatScorOutcomeLabel(showcase?.pool?.with_outcome)}</span></div>
        </aside>
      </div>
      {whileWaiting.length > 0 && <section className={styles.card}><div className={styles.cardHeader}><div><h2 className={styles.sectionTitle}>{scorT('whileYourReportIsBeingPrepared')}</h2><div className={styles.muted}>{scorT('similarCasesFromTheReportLibrary')}</div></div></div><div className={styles.list}>{whileWaiting.map((report) => <ReportRow key={report.id} report={report} href={AppRoute.scorPoolDetail(report.id ?? 0)} />)}</div></section>}
    </div>
  );
};

const OrderPage: FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const categoriesQuery = useFetchScorCategoriesQuery();
  const pricingQuery = useFetchScorPricingQuery();
  const subscriptionQuery = useFetchScorSubscriptionQuery();
  const documentsQuery = useFetchScorDocumentsQuery(undefined, { refetchOnMountOrArgChange: true });
  const [createRequest, { isLoading }] = useCreateScorRequestMutation();
  const creatingRef = useRef(false);
  const toast = useToast();
  const toastError = useScorToastError();
  const navigate = useNavigate();
  const uploadRules = categoriesQuery.data?.data?.upload;
  const categories = categoriesQuery.data?.data?.categories ?? [];
  const services = categoriesQuery.data?.data?.services ?? [];
  const [categoryId, setCategoryId] = useState(0);
  const [serviceId, setServiceId] = useState(0);
  const [issuer, setIssuer] = useState('');
  const [comment, setComment] = useState('');
  const [sum, setSum] = useState('');
  const [consent, setConsent] = useState(false);
  useEffect(() => { setConsent(false); }, [documentsQuery.fulfilledTimeStamp, documentsQuery.data?.data?.accepted_required]);
  const [documents, setDocuments] = useState<File[]>([]);
  const [legalDocument, setLegalDocument] = useState<ApiScorLegalDocument>();
  const [fieldsByCategory, setFieldsByCategory] = useState<
    Record<number, Record<string, string | number | boolean>>
  >({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const category = categories.find((item) => item.id === categoryId);
  const categoryFields = category?.fields ?? [];
  const fields = fieldsByCategory[categoryId] ?? {};
  const selectedService = services.find((item) => item.id === serviceId);
  const selectedPricingService = pricingQuery.data?.data?.services?.find((item) =>
    item.id === serviceId || (item.code && item.code === selectedService?.code));
  const subscription = subscriptionQuery.data?.data?.subscription;
  const documentsReady = !documentsQuery.isFetching && !documentsQuery.isError && typeof documentsQuery.data?.data?.accepted_required === 'boolean';
  const hasAcceptedDocuments = documentsQuery.data?.data?.accepted_required === true;
  const offerDocument = documentsQuery.data?.data?.items?.find((item) => item.type === 1);
  const getOrderErrors = (overrides: Partial<ScorOrderValidationValues> = {}): Record<string, string> => {
    const errors = validateScorOrder({
      categoryId,
      serviceId,
      issuer,
      comment,
      sum,
      consent: hasAcceptedDocuments || consent,
      documentCount: documents.length,
      documentsRequired: uploadRules?.is_required !== false,
      categoryFields,
      fields,
      ...overrides,
    });
    const fileError = validateScorFiles(documents, uploadRules, uploadRules?.is_required !== false);
    if (fileError && overrides.documentCount === undefined) errors.documents = fileError;

    return errors;
  };
  const updateFieldError = (key: string, overrides: Partial<ScorOrderValidationValues>): void => {
    const errors = getOrderErrors(overrides);
    setFieldErrors((current) => {
      if (!current[key]) return current;

      return errors[key]
        ? { ...current, [key]: errors[key] }
        : Object.fromEntries(Object.entries(current).filter(([field]) => field !== key));
    });
  };

  useEffect(() => {
    const firstCategoryId = categories[0]?.id;
    if (!categoryId && firstCategoryId) setCategoryId(firstCategoryId);
  }, [categories, categoryId]);

  useEffect(() => {
    const firstServiceId = services[0]?.id;
    if (!serviceId && firstServiceId) setServiceId(firstServiceId);
  }, [serviceId, services]);

  const addDocuments = (files: File[]): void => {
    const next = [...documents];
    files.forEach((file) => {
      if (!next.some((item) => item.name === file.name && item.size === file.size && item.lastModified === file.lastModified)) next.push(file);
    });
    const error = validateScorFiles(next, uploadRules, uploadRules?.is_required !== false);
    setFieldErrors((current) => ({ ...current, documents: error ?? '' }));
    if (!error) setDocuments(next);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    addDocuments(Array.from(event.dataTransfer.files));
  };

  const setDynamicField = (code: string | undefined, value: string | number | boolean): void => {
    if (!code) return;
    setFieldsByCategory((state) => ({
      ...state,
      [categoryId]: { ...state[categoryId], [code]: value },
    }));
    updateFieldError(`fields.${code}`, { fields: { ...fields, [code]: value } });
  };

  const slaLabel = category?.sla_days
    ? formatScorWorkingDays(category.sla_days)
    : selectedService?.sla_hours
      ? scorT('hours', { v0: selectedService.sla_hours })
      : scorT('timingConfirmedWithYourRequest');

  const servicePrice = selectedPricingService?.price_for_me ??
    selectedPricingService?.price ??
    selectedService?.price;
  const isIncluded = Boolean(
    subscription?.is_active &&
    (subscription.reports_left ?? 0) > 0 &&
    !selectedPricingService?.is_surcharge
  );
  const orderPaymentLabel = serviceId
    ? isIncluded
      ? scorT('includedInYourSubscription')
      : typeof servicePrice === 'number'
        ? scorT('amountDue', { v0: formatMoney(servicePrice) })
        : scorT('priceOnRequest')
    : scorT('selectAReportType');

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (creatingRef.current || isLoading || !documentsReady) return;
    const errors = getOrderErrors();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      const hasMissingRequired = !categoryId || !serviceId || !issuer.trim() ||
        (!hasAcceptedDocuments && !consent) || (uploadRules?.is_required !== false && documents.length === 0) ||
        categoryFields.some((field) => field.is_required && errors[`fields.${field.code}`]);
      toast({ title: hasMissingRequired ? scorT('completeTheRequiredFields') : scorT('checkTheFormFields'), status: 'warning', position: 'top-right' });

      return;
    }
    creatingRef.current = true;
    try {
      const requestFields = Object.fromEntries(
        categoryFields.flatMap((field) => {
          if (!field.code) return [];
          const value = fields[field.code];
          if (value === undefined || value === '') return [];
          if (field.type === 'select' && !(field.options ?? []).includes(String(value))) return [];

          return [[field.code, value] as const];
        })
      );
      const response = await createRequest({
        category_id: categoryId,
        service_id: serviceId,
        issuer: issuer.trim(),
        comment: comment || undefined,
        sum_deal_rub: sum ? Number(sum) : undefined,
        consent: hasAcceptedDocuments || consent,
        fields: requestFields,
        documents,
        uploadField: uploadRules?.field,
      }).unwrap();
      if (response.data?.invoice?.id) {
        navigate(AppRoute.scorInvoice(response.data.invoice.id), { state: { invoiceId: response.data.invoice.id, orderPrice: response.data.price } });

        return;
      }
      toast({ title: response.data?.message ?? scorT('requestCreated'), status: 'success', position: 'top-right' });
      navigate(AppRoute.scor);
    } catch (error) {
      const nextFieldErrors = getScorFieldErrors(error);
      if (Object.keys(nextFieldErrors).length > 0) setFieldErrors(nextFieldErrors);
      else toastError(error);
    } finally {
      creatingRef.current = false;
    }
  };

  return (
    <div className={styles.stack}>
      <div>
        <div className={styles.screenKicker}>{scorT('scorNewAnalyticalReport')}</div>
        <div>
          <section className={styles.orderHero}>
            <div className={styles.orderHeroCopy}>
              <div className={styles.referralEyebrow}><i />{' '}{scorT('reviewBeforeInvesting')}</div>
              <h1>{scorT('shareTheDocumentsWeWillTurnThem')}</h1>
              <p>{scorT('anAnalystWillReviewTheOfferS')}</p>
              <div className={styles.orderBenefits}>
                <span><i /> {slaLabel}</span>
                <span><i />{' '}{scorT('confidential')}</span>
                <span><i />{' '}{scorT('reportInTheApp')}</span>
              </div>
            </div>
            <div className={styles.orderHeroVisual}>
              <img src={orderImage} alt={scorT('investmentDocumentReview')} />
              <div className={styles.orderHeroChip}><span>{scorT('reviewTerms')}</span><b>{orderPaymentLabel}</b></div>
            </div>
          </section>
        </div>
      </div>

      <section className={styles.orderFormCard}>
        <div className={styles.orderFormHeader}>
          <div><h2>{scorT('offerDetails')}</h2><p>{orderPaymentLabel}{scorT('yourRequestIsSubmittedOnlyAfterYou')}</p></div>
        </div>

        {(categoriesQuery.isLoading || pricingQuery.isLoading) && <div className={styles.empty}><Spinner color="primary.600" /></div>}
        {(categoriesQuery.isError || pricingQuery.isError) && (
          <div className={styles.orderLoadError}>
            <div><b>{scorT('unableToLoadReviewSettings')}</b><span>{scorT('pleaseTryAgainLater')}</span></div>
            <Button size="sm" variant="outline" onClick={() => { void categoriesQuery.refetch(); void pricingQuery.refetch(); }}>{scorT('retry')}</Button>
          </div>
        )}
        {!categoriesQuery.isLoading && !pricingQuery.isLoading && !categoriesQuery.isError && !pricingQuery.isError && (categories.length === 0 || services.length === 0) && (
          <div className={styles.empty}>{scorT('noCategoriesOrReportTypesAreCurrently')}</div>
        )}

        {!categoriesQuery.isLoading && !pricingQuery.isLoading && !categoriesQuery.isError && !pricingQuery.isError && categories.length > 0 && services.length > 0 && (
          <form noValidate className={styles.orderForm} onSubmit={(event) => { void submit(event); }}>
            <div className={styles.orderFieldFull}>
              <span className={styles.fieldLabel}>{scorT('whatAreWeReviewing')}</span>
              <div className={styles.orderSegments} role="radiogroup" aria-label={scorT('reportCategory')} aria-invalid={Boolean(fieldErrors.category_id)}>
                {categories.map((item) => <button key={item.id} type="button" aria-pressed={item.id === categoryId} className={item.id === categoryId ? styles.orderSegmentActive : undefined} onClick={() => { if (!item.id) return; setCategoryId(item.id); setFieldErrors((current) => Object.fromEntries(Object.entries(current).filter(([key]) => key !== 'category_id' && !key.startsWith('fields.')))); }}>{scorMetadataText(item.name)}</button>)}
              </div>
              {fieldErrors.category_id && <small className={styles.fieldError}>{fieldErrors.category_id}</small>}
            </div>

            <div className={styles.orderFieldFull}>
              <span className={styles.fieldLabel}>{scorT('reportType')}</span>
              <div className={`${styles.orderSegments} ${styles.orderServiceSegments}`} role="radiogroup" aria-label={scorT('reportType121')} aria-invalid={Boolean(fieldErrors.service_id)}>
                {services.map((item) => {
                  const pricingService = pricingQuery.data?.data?.services?.find((service) => service.id === item.id || (service.code && service.code === item.code));
                  const price = pricingService?.price_for_me ?? pricingService?.price ?? item.price;

                  return <button key={item.id} type="button" aria-pressed={item.id === serviceId} className={item.id === serviceId ? styles.orderSegmentActive : undefined} onClick={() => { if (item.id) setServiceId(item.id); setFieldErrors((current) => ({ ...current, service_id: '' })); }}><b>{item.name}</b><span>{item.sla_hours ? scorT('hrs', { v0: item.sla_hours }) : item.description ?? scorT('standardTurnaround')} · {typeof price === 'number' ? formatMoney(price) : scorT('onRequest124')}</span></button>;
                })}
              </div>
              {fieldErrors.service_id && <small className={styles.fieldError}>{fieldErrors.service_id}</small>}
            </div>

            <label className={styles.orderFieldFull}><span className={styles.fieldLabel}>{scorT('productNameOrIssuer')}</span><input required maxLength={SCOR_ISSUER_MAX_LENGTH} aria-invalid={Boolean(fieldErrors.issuer)} aria-describedby={fieldErrors.issuer ? 'scor-issuer-error' : undefined} className={styles.nativeInput} value={issuer} onChange={(event) => { setIssuer(event.target.value); updateFieldError('issuer', { issuer: event.target.value }); }} placeholder={scorT('forExampleAgrotechPreIpo')} />{fieldErrors.issuer && <small id="scor-issuer-error" className={styles.fieldError}>{fieldErrors.issuer}</small>}</label>
            <label>
              <span className={styles.fieldLabel}>{scorT('investmentAmountRub')}{' '}<em>{scorT('optional')}</em></span>
              <input className={styles.nativeInput} aria-invalid={Boolean(fieldErrors.sum_deal_rub)} min="0" step="any" type="number" value={sum} onChange={(event) => { setSum(event.target.value); updateFieldError('sum_deal_rub', { sum: event.target.value }); }} />
              {fieldErrors.sum_deal_rub && <small className={styles.fieldError}>{fieldErrors.sum_deal_rub}</small>}
            </label>

            {categoryFields.map((field) => {
              const code = field.code ?? '';
              const value = fields[code];
              const fieldClassName = field.type === 'text' ? styles.orderFieldFull : undefined;
              const fieldError = fieldErrors[`fields.${code}`];
              const errorId = `scor-field-${code}-error`;

              if (field.type === 'select') {
                const options = field.options ?? [];

                return <div key={code} className={fieldClassName}><span className={styles.fieldLabel}>{field.label}{field.is_required ? ' *' : <em>{scorT('optional')}</em>}</span>{options.length > 0 ? <div className={styles.orderSegments} role="radiogroup" aria-label={field.label} aria-invalid={Boolean(fieldError)} aria-describedby={fieldError ? errorId : undefined}>{options.map((option) => <button key={option} type="button" role="radio" aria-checked={value === option} className={value === option ? styles.orderSegmentActive : undefined} onClick={() => setDynamicField(field.code, option)}>{option}</button>)}</div> : <div className={styles.orderFieldUnavailable} aria-invalid={Boolean(fieldError)} role="alert"><div><b>{scorT('optionsCouldNotBeLoaded')}</b><span>{scorT('theServiceDidNotProvideOptionsFor')}{field.label}».</span></div><Button type="button" size="sm" variant="outline" onClick={() => { void categoriesQuery.refetch(); }}>{scorT('retry')}</Button></div>}{field.hint && <small className={styles.orderHint}>{field.hint}</small>}{fieldError && <small id={errorId} className={styles.fieldError}>{fieldError}</small>}</div>;
              }

              if (field.type === 'bool') {
                return <label key={code} className={`${styles.orderCheck} ${fieldClassName ?? ''}`}><input type="checkbox" required={field.is_required} aria-invalid={Boolean(fieldError)} aria-describedby={fieldError ? errorId : undefined} checked={Boolean(value)} onChange={(event) => setDynamicField(field.code, event.target.checked)} /><span>{field.label}{field.is_required ? ' *' : ''}{field.hint && <small>{field.hint}</small>}{fieldError && <small id={errorId} className={styles.fieldError}>{fieldError}</small>}</span></label>;
              }

              if (field.type === 'text') {
                return <label key={code} className={fieldClassName}><span className={styles.fieldLabel}>{field.label}{field.is_required ? ' *' : <em>{scorT('optional')}</em>}</span><textarea className={styles.nativeTextarea} required={field.is_required} aria-invalid={Boolean(fieldError)} aria-describedby={fieldError ? errorId : undefined} value={String(value ?? '')} onChange={(event) => setDynamicField(field.code, event.target.value)} />{field.hint && <small className={styles.orderHint}>{field.hint}</small>}{fieldError && <small id={errorId} className={styles.fieldError}>{fieldError}</small>}</label>;
              }

              return <label key={code} className={fieldClassName}><span className={styles.fieldLabel}>{field.label}{field.is_required ? ' *' : <em>{scorT('optional')}</em>}</span><input className={styles.nativeInput} required={field.is_required} aria-invalid={Boolean(fieldError)} aria-describedby={fieldError ? errorId : undefined} step={field.type === 'number' || field.type === 'money' ? 'any' : undefined} type={field.type === 'number' || field.type === 'money' ? 'number' : field.type === 'date' ? 'date' : 'text'} value={String(value ?? '')} onChange={(event) => setDynamicField(field.code, event.target.value)} />{field.hint && <small className={styles.orderHint}>{field.hint}</small>}{fieldError && <small id={errorId} className={styles.fieldError}>{fieldError}</small>}</label>;
            })}

            <div className={styles.orderFieldFull}>
              <span id="scor-documents-label" className={styles.fieldLabel}>{scorT('documents')}{uploadRules?.is_required !== false ? ' *' : ''}</span>
              <div className={styles.orderDropzone} role="group" aria-labelledby="scor-documents-label" aria-invalid={Boolean(fieldErrors.documents)} aria-describedby={fieldErrors.documents ? 'scor-documents-error' : undefined} onDragOver={(event) => event.preventDefault()} onDrop={handleDrop}>
                <span className={styles.orderUploadIcon}><FiFileText /></span>
                <div>
                  <b>{scorT('dragDocumentsHereForReview')}</b>
                  <small>{uploadRules?.hint}</small>
                  {Boolean(uploadRules?.extensions?.length) && <small>{scorT('acceptedFormats')}{' '}{uploadRules?.extensions.map((extension) => extension.toUpperCase()).join(', ')}.</small>}
                </div>
                <Button type="button" size="sm" variant="outline" onClick={() => fileInputRef.current?.click()}>{scorT('chooseFiles')}</Button>
                <input ref={fileInputRef} hidden multiple accept={uploadRules?.accept} disabled={!hasScorUploadRules(uploadRules)} type="file" onChange={(event) => { addDocuments(Array.from(event.target.files ?? [])); event.target.value = ''; }} />
              </div>
              {!hasScorUploadRules(uploadRules) && <div role="alert">{scorT('unableToLoadUploadSettings')}{' '}<button type="button" onClick={() => { void categoriesQuery.refetch(); }}>{scorT('reload')}</button></div>}
              {documents.length > 0 && <div className={styles.orderFiles}>{documents.map((file) => <div key={`${file.name}-${file.lastModified}`}><FiFileText /><span>{file.name}</span><button type="button" aria-label={scorT('remove', { v0: file.name })} onClick={() => { const next = documents.filter((item) => item !== file); setDocuments(next); setFieldErrors((current) => ({ ...current, documents: validateScorFiles(next, uploadRules, uploadRules?.is_required !== false) ?? '' })); }}>×</button></div>)}</div>}
              {fieldErrors.documents && <small id="scor-documents-error" className={styles.fieldError}>{fieldErrors.documents}</small>}
            </div>

            <label className={styles.orderFieldFull}><span className={styles.fieldLabel}>{scorT('comment')}{' '}<em>{scorT('optional')}</em></span><textarea className={styles.nativeTextarea} maxLength={SCOR_COMMENT_MAX_LENGTH} aria-invalid={Boolean(fieldErrors.comment)} aria-describedby={fieldErrors.comment ? 'scor-comment-error' : undefined} value={comment} onChange={(event) => { setComment(event.target.value); updateFieldError('comment', { comment: event.target.value }); }} placeholder={scorT('whatConcernsYouOrNeedsParticularAttention')} />{fieldErrors.comment && <small id="scor-comment-error" className={styles.fieldError}>{fieldErrors.comment}</small>}</label>
            {documentsQuery.data?.data?.items && documentsQuery.data.data.items.length > 0 && <div className={`${styles.orderLegalLinks} ${styles.orderFieldFull}`}>{documentsQuery.data.data.items.filter((item) => item.type === 1).map((item) => hasLegalDocumentContent(item) ? <button key={item.id} type="button" onClick={() => setLegalDocument(item)}>{item.title}</button> : <span key={item.id}>{item.title}</span>)}</div>}
            {!documentsReady ? <div role="alert">{scorT('currentTermsUnavailable')} <Button onClick={() => { void documentsQuery.refetch(); }}>{scorT('retry')}</Button></div> : hasAcceptedDocuments ? <div className={`${styles.orderConsentAccepted} ${styles.orderFieldFull}`}><FiCheck />{' '}{scorT('youHaveAlreadyAcceptedTheCurrentDocument')}</div> : <label className={`${styles.orderCheck} ${styles.orderFieldFull}`}><input required type="checkbox" aria-invalid={Boolean(fieldErrors.consent)} aria-describedby={fieldErrors.consent ? 'scor-consent-error' : undefined} checked={consent} onChange={(event) => { setConsent(event.target.checked); updateFieldError('consent', { consent: event.target.checked }); }} /><span>{scorT('iAcceptTheOffer')}{fieldErrors.consent && <small id="scor-consent-error" className={styles.fieldError}>{fieldErrors.consent}</small>}</span></label>}
            {offerDocument && hasLegalDocumentContent(offerDocument) && <div className={`${styles.orderLegalLinks} ${styles.orderFieldFull}`}><button type="button" onClick={() => setLegalDocument(offerDocument)}>{scorT('theOfferContainsTheTermsForAdding')}</button></div>}
            <PricingDocument document={pricingQuery.data?.data?.document} /><div className={styles.orderSubmit}><Button isDisabled={!documentsReady} type="submit" colorScheme="green" isLoading={isLoading}>{isIncluded ? scorT('orderReportNow') : scorUiText(pricingQuery.data?.data?.payment?.cta, 'payNow')}</Button><span>{scorT('preparationTime')}{' '}{slaLabel}{scorT('theAnalystWillContactYouIfMore')}</span></div>
          </form>
        )}
      </section>
      <LegalDocumentDialog item={legalDocument && (documentsQuery.data?.data?.items?.find((item) => item.id === legalDocument.id) ?? legalDocument)} onClose={() => setLegalDocument(undefined)} />
    </div>
  );
};

const ReportsPage: FC = () => {
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, error } = useFetchScorMyReportsQuery({ page, limit: 50 });
  const orders = useFetchScorMyOrdersQuery();
  const reports = data?.data?.items ?? [];
  const pendingOrders = normalizeScorOrders(orders.data?.data?.items ?? []);

  return <div className={`${styles.stack} ${styles.myReportsPage}`}><SectionHeader title={scorT('myReports')} description={scorT('ongoingRequestsAndPublishedScorReports')} action={<Button as={Link} to={AppRoute.scorOrder} colorScheme="green">{scorT('newAssessment')}</Button>} />{pendingOrders.length > 0 && <section className={`${styles.card} ${styles.compactListCard}`}><h2 className={styles.sectionTitle}>{scorT('requestsInProgress')}</h2><div className={styles.list}>{pendingOrders.map((order) => <div className={`${styles.orderRow} ${styles.orderWithFollowUp}`} key={order.request_id != null ? `request-${order.request_id}` : order.id}><div><div className={styles.reportName}>{order.issuer ?? order.service ?? scorT('request146', { v0: order.id ?? '—' })}</div><div className={styles.muted}>{order.status_label ?? (order.id ? scorT('inProgress') : scorT('underReview'))}{''}</div></div><OrderActions order={order} showFollowUp={false} />{order.id && order.needs_action && <OrderFollowUp order={order} />}</div>)}</div></section>}<section className={`${styles.card} ${styles.compactListCard}`}><h2 className={styles.sectionTitle}>{scorT('completedReports')}</h2><QueryState isLoading={isLoading || orders.isLoading} isError={isError || orders.isError} error={error ?? orders.error} isEmpty={reports.length === 0 && pendingOrders.length === 0}>{reports.length > 0 ? <div className={styles.list}>{reports.map((report) => <ReportRow key={report.id} report={report} href={AppRoute.scorReport(report.id ?? 0)} />)}</div> : <div className={styles.compactEmpty}>{scorT('noCompletedReportsYet')}</div>}</QueryState><ScorPagination page={page} pagination={data?.data?.pagination} onPage={setPage} /></section></div>;
};

const ReportParameters: FC<{ value: unknown }> = ({ value }) => {
  if (value == null) return <>—</>;
  if (typeof value === 'boolean') return <>{value ? scorT('yes') : scorT('no')}</>;
  if (typeof value !== 'object') return <>{String(value)}</>;
  if (Array.isArray(value)) return <ul>{value.map((item, index) => <li key={index}><ReportParameters value={item} /></li>)}</ul>;

  return <dl className={styles.reportParameters}>{Object.entries(value).map(([key, item]) => <div key={key}><dt>{key}</dt><dd><ReportParameters value={item} /></dd></div>)}</dl>;
};

const ReportContent: FC<{ report: ApiScorMyReportDetail | ApiScorPoolReportDetail; reportOrder?: ApiScorOrder }> = ({ report, reportOrder }) => {
  const questions = 'questions' in report ? report.questions ?? [] : [];

  return <div className={styles.stack}>{report && <><section className={`${styles.card} ${styles.detailHero}`}><ReportCover className={styles.detailCover} report={report} /><div><div className={styles.eyebrow}>{scorT('independentAssessmentResults')}</div><h2 className={styles.sectionTitle}>{report.forecast ?? scorT('investmentProductReport')}</h2><p className={styles.heroText}>{report.promised_yield ? scorT('advertisedReturn', { v0: report.promised_yield }) : scorT('conclusionsAreBasedOnDocumentsAndIndependent')}</p><div className={styles.buttonRow}>{report.can_share && <Button as={Link} to={`${AppRoute.scorShare}?report=${report.id}`} variant="outline" leftIcon={<FiSend />}>{scorT('share')}</Button>}{reportOrder && <OrderActions order={reportOrder} />}</div></div><div className={`${styles.detailScore} ${getScoreClassName(report.score)}`}>{report.score ?? '—'}</div></section><div className={styles.reportColumns}><div className={styles.reportMain}><ScorReportSections report={report} />{Boolean(report.summary?.length) && <section className={styles.card}><h2 className={styles.sectionTitle}>{scorT('keyFindings')}</h2><div className={styles.featureList}>{report.summary?.map((item) => <div className={styles.feature} key={item}><span className={styles.featureIcon}><FiCheck /></span><span className={styles.multilineText}>{normalizeScorLineBreaks(item)}</span></div>)}</div></section>}{report.score_details && Object.keys(report.score_details).length > 0 && <section className={styles.card}><h2 className={styles.sectionTitle}>{scorT('scoresByCriterion')}</h2><ReportParameters value={report.score_details} /></section>}</div><div className={styles.reportAside}><ScorReportFiles key={report.id} report={report} />{report.outcome != null && <section className={`${styles.card} ${styles.outcomeCard}`}><h2 className={styles.sectionTitle}>{scorT('outcome')}</h2><p className={styles.muted}>{scorMetadataText(report.outcome?.label) ?? scorT('stillBeingMonitored')}</p><div className={styles.metricValue}>{report.outcome?.actual ?? '—'}</div>{report.outcome?.comment && <p>{report.outcome.comment}</p>}<p className={`${styles.muted} ${styles.outcomeCheckedAt}`}>{scorT('checked')}{' '}{formatDate(report.outcome?.checked_at)}</p></section>}</div></div>{questions.length > 0 && <section className={styles.card}><h2 className={styles.sectionTitle}>{scorT('questionsForTheSeller')}</h2><div className={styles.featureList}>{questions.map((item) => <div className={styles.reportQuestion} key={item.id ?? item.question}><b className={styles.multilineText}>{normalizeScorLineBreaks(item.question ?? '')}</b>{item.comment && <span className={styles.multilineText}>{normalizeScorLineBreaks(item.comment)}</span>}</div>)}</div></section>}<ScorReportHistory report={report} /></>}</div>;
};

const ReportDetail: FC<{ pool?: boolean }> = ({ pool = false }) => {
  const { pathname } = useLocation();
  const numericId = getNumberParam(getLastPathSegment(pathname));
  const own = useFetchScorReportDetailQuery({ id: numericId }, {
    skip: pool || !numericId,
    refetchOnMountOrArgChange: true,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });
  const pooled = useFetchScorPoolDetailQuery({ id: numericId }, { skip: !pool || !numericId });
  const orders = useFetchScorMyOrdersQuery(undefined, { skip: pool });
  const query = pool ? pooled : own;
  const report = query.data?.data?.item;
  const reportOrder = orders.data?.data?.items?.find((order) => order.report_id === report?.id);

  return <><SectionHeader title={report?.issuer ?? scorT('scorReport')} description={[scorMetadataText(report?.category), report?.public_code].filter(Boolean).join(' · ')} action={<Button as={Link} to={pool ? AppRoute.scorPool : AppRoute.scorReports} variant="outline">{scorT('back')}</Button>} /><QueryState isLoading={query.isLoading} isError={query.isError} error={query.error} isEmpty={!report}>{report && <ReportContent report={pool ? report : { ...report, is_mine: true }} reportOrder={reportOrder} />}</QueryState></>;
};

const PoolPage: FC = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = window.setTimeout(() => { setDebouncedSearch(search.trim()); setPage(1); }, 300);

    return () => window.clearTimeout(timer);
  }, [search]);
  const [withOutcome, setWithOutcome] = useState(false);
  const [categoryId, setCategoryId] = useState(0);
  const showcaseQuery = useFetchScorShowcaseQuery();
  const categoriesQuery = useFetchScorCategoriesQuery();
  const { data, isLoading, isError, error } = useFetchScorPoolQuery({
    page,
limit: 20,
    search: debouncedSearch || undefined,
    category_id: categoryId || undefined,
    with_outcome: withOutcome || undefined,
  });
  const reports = data?.data?.items ?? [];
  const categories = categoriesQuery.data?.data?.categories ?? [];
  const total = showcaseQuery.data?.data?.pool?.total ?? '—';
  const totalWithOutcome = showcaseQuery.data?.data?.pool?.with_outcome ?? '—';
  const scores = reports
    .map((report) => report.score)
    .filter((score): score is number => typeof score === 'number')
    .sort((left, right) => left - right);
  const middle = Math.floor(scores.length / 2);
  const median = scores.length === 0
    ? undefined
    : scores.length % 2 === 0
      ? (scores[middle - 1] + scores[middle]) / 2
      : scores[middle];

  return (
    <div className={styles.poolPage}>
      <SectionHeader
        eyebrow={scorT('anonymizedAnalysis')}
        title={scorT('reportLibrary')}
        description={scorT('investmentProductsWithIndependentScorAssessmentsAnd')}
        action={(
          <div className={styles.poolPrimaryFilters}>
            <button
              type="button"
              className={!withOutcome ? styles.poolFilterActive : undefined}
              onClick={() => { setWithOutcome(false); setPage(1); }}
            >{scorT('all')}{' '}{total}
            </button>
            <button
              type="button"
              className={withOutcome ? styles.poolFilterActive : undefined}
              onClick={() => { setWithOutcome(true); setPage(1); }}
            >{scorT('withOutcomes')}{' '}{totalWithOutcome}
            </button>
          </div>
        )}
      />

      <div className={styles.poolSummary}>
        <div><span>{scorT('inTheLibrary')}</span><b>{total}</b></div>
        <div><span>{scorT('outcomeRecorded')}</span><b>{totalWithOutcome}</b></div>
        <div><span>{scorT('inThisSelection')}</span><b>{reports.length}</b></div>
        <div><span>{scorT('medianScore')}</span><b>{typeof median === 'number' ? median.toLocaleString(getScorLocale(), { maximumFractionDigits: 1 }) : '—'}</b></div>
      </div>

      <label className={styles.librarySearch}><span>{scorT('librarySearch')}</span><input type="search" className={styles.nativeInput} value={search} onChange={(event) => setSearch(event.target.value)} /></label>
      <div className={styles.poolToolbar} aria-label={scorT('reportCategories')}>
        <button
          type="button"
          className={categoryId === 0 ? styles.poolCategoryActive : undefined}
          onClick={() => { setCategoryId(0); setPage(1); }}
        >{scorT('all174')}</button>
        {categories.map((category) => (
          <button
            type="button"
            key={category.id ?? category.code}
            className={categoryId === category.id ? styles.poolCategoryActive : undefined}
            onClick={() => { setCategoryId(category.id ?? 0); setPage(1); }}
          >
            {scorMetadataText(category.name) ?? category.code ?? scorT('category')}
          </button>
        ))}
      </div>

      <div className={styles.poolResults}>
        {data?.data?.full_access === false && isSubscriptionSaleEnabled(data?.data) && (
          <section className={`${styles.card} ${styles.poolAccessNotice}`}>
            <p>{scorT('youHaveAccessToPartOfThe')}</p>
            <SubscriptionAction />
          </section>
        )}
        <QueryState
          isLoading={isLoading || categoriesQuery.isLoading}
          isError={isError} error={error}
          isEmpty={reports.length === 0}
          emptyText={scorT('noPublishedReportsInThisSelectionYet')}
        >
          <div className={styles.poolReviewList}>
            {reports.map((report) => {
              const scoreClass = getScoreClassName(report.score);

              return (
                <Link
                  className={styles.poolReviewCard}
                  key={report.id}
                  to={AppRoute.scorPoolDetail(report.id ?? 0)}
                >
                  <div
                    className={styles.poolReviewCover}
                    style={report.cover_color ? { backgroundColor: report.cover_color } : undefined}
                  >
                    <ReportCover className={styles.poolReviewCoverMedia} report={report} />
                    <span className={styles.poolReviewBadge}>{scorMetadataText(report.category) ?? scorT('scorReport83')}</span>
                  </div>

                  <div className={styles.poolReviewMain}>
                    <span className={styles.poolReviewId}>
                      SCOR LIBRARY · {report.public_code ?? `№ ${report.id ?? '—'}`}
                    </span>
                    <div className={styles.poolReviewTitle}>
                      <h2>{report.issuer ?? scorT('investmentProduct')}</h2>
                      <span className={hasScorOutcome(report) ? styles.poolOutcomeKnown : styles.poolOutcomePending}>
                        {scorMetadataText(report.outcome?.label) ?? (hasScorOutcome(report) ? scorT('outcomeRecorded') : scorT('beingMonitored'))}
                      </span>
                    </div>
                    <p>
                      {report.outcome?.actual
                        ? scorT('actualOutcome', { v0: report.outcome.actual })
                        : scorT('anAnonymizedAnalysisOfTheProductS')}
                    </p>
                    <div className={styles.poolReviewSpecs}>
                      {scorMetadataText(report.category) && <span>{scorT('category')}{' '}<b>{scorMetadataText(report.category)}</b></span>}
                      {scorMetadataText(report.sale_channel) && <span>{scorT('channel')}{' '}<b>{scorMetadataText(report.sale_channel)}</b></span>}
                      {report.outcome?.checked_at && <span>{scorT('checked182')}{' '}<b>{formatDate(report.outcome.checked_at)}</b></span>}
                    </div>
                  </div>

                  <div className={styles.poolReviewSide}>
                    <div className={styles.poolReviewScore}>
                      <span>SCOR</span>
                      <b className={scoreClass}>{report.score?.toLocaleString(getScorLocale()) ?? '—'}</b>
                      <small>{hasScorOutcome(report) ? scorT('actualOutcomeRecorded') : scorT('monitoringContinues')}</small>
                    </div>
                    <span className={styles.poolOpenLink}>{scorT('reportDetails')}{' '}<FiArrowRight /></span>
                  </div>
                </Link>
              );
            })}
          </div>
        </QueryState>
        <ScorPagination page={page} pagination={data?.data?.pagination} onPage={setPage} />
      </div>
    </div>
  );
};

const SharedPage: FC = () => {
  const { data, isLoading, isError, error } = useFetchScorSharedWithMeQuery();
  const reports = data?.data?.items ?? [];

  return <><SectionHeader title={scorT('availableToMe')} description={scorT('reportsSharedWithYouByOtherUsers')} /><section className={styles.card}><QueryState isLoading={isLoading} isError={isError} error={error} isEmpty={reports.length === 0}><div className={styles.list}>{reports.map((report) => <ReportRow key={report.id} report={report} actionLabel="open" href={AppRoute.scorPoolDetail(report.id ?? 0)} />)}</div></QueryState></section></>;
};

export const ScorSharedEntry: FC = () => {
  useTranslation();
  const auth = useAppSelector(authSelector);
  const location = useLocation();
  const token = getLastPathSegment(location.pathname);
  const loggedIn = auth.status === AuthStatus.LoggedIn;
  const preview = useFetchScorSharedPreviewQuery({ token }, { skip: loggedIn || !token });
  if (loggedIn) return <ScorPage />;
  const data = preview.data?.data;

  return <Container maxW="800px" py="40px"><main className={styles.stack}>
    <SectionHeader title={scorT('invitationPreview')} />
    {preview.isError && <Button variant="outline" onClick={() => { void preview.refetch(); }}>{scorT('reload')}</Button>}
    <QueryState isLoading={preview.isLoading} isError={preview.isError} error={preview.error}>
      {data?.is_valid === false ? <p role="status">{scorT('invalidInvitation')}</p> : data?.is_valid && <section className={styles.card}>
        <h2 className={styles.sectionTitle}>{data.issuer}</h2>
        {data.note && <p className={styles.multilineText}>{data.note}</p>}
        {data.code && <ScorCopy value={data.code} label={scorT('copyInviteCode')} />}
        <Button as={Link} to={AppRoute.login} state={{ from: location }} colorScheme="green">{scorT('loginForReport')}</Button>
      </section>}
    </QueryState>
  </main></Container>;
};

const SharedViewPage: FC = () => {
  const { pathname } = useLocation();
  const token = getLastPathSegment(pathname);
  const { data, isLoading, isError, error } = useFetchSharedScorReportQuery({ token }, { skip: !token });
  const report = data?.data?.item;
  if (typeof error === 'object' && error !== null && 'status' in error && error.status === 404) return <p role="status">{scorT('invalidInvitation')}</p>;

  return <><SectionHeader title={report?.issuer ?? scorT('sharedScorReport')} description={data?.data?.shared_by?.note ?? scorT('protectedAccessByInvitation')} /><QueryState isLoading={isLoading} isError={isError} error={error} isEmpty={!report}>{report && <ReportContent report={report} />}</QueryState></>;
};

const FindPage: FC = () => {
  const saleEnabled = useSubscriptionSaleEnabled();
  const [code, setCode] = useState('');
  const [submitted, setSubmitted] = useState('');
  const result = useFindScorByCodeQuery({ code: submitted }, { skip: !submitted });
  const submit = (event: FormEvent): void => { event.preventDefault(); if (code.trim()) { if (submitted === code.trim()) void result.refetch(); else setSubmitted(code.trim()); } };
  const item = result.data?.data?.has_access === true ? result.data.data.item : undefined;
  const preview = result.data?.data?.preview;
  const needsSubscription = saleEnabled && result.data?.data?.has_access === false && result.data.data.needs_subscription === true;

  return <>
    <SectionHeader title={scorT('findAReportByCode')} description={scorT('checkAReportSAvailabilityAndYour')} />
    <section className={`${styles.card} ${styles.findCard}`}>
      <form className={styles.findForm} onSubmit={submit}>
        <label className={styles.findField}>
          <span className={styles.fieldLabel}>{scorT('reportOrInviteCode')}</span>
          <input className={styles.nativeInput} value={code} onChange={(event) => setCode(event.target.value)} placeholder={scorT('codeExample')} />
        </label>
        <Button type="submit" colorScheme="green" height="46px" isLoading={result.isFetching} leftIcon={<FiSearch />}>{scorT('find')}</Button>
      </form>
      {(result.isError || Boolean(preview) || Boolean(item) || needsSubscription) && <div className={styles.findResult} aria-live="polite">
        {result.isError && <ScorError error={result.error} />}
        {preview && !item && <div className={styles.linkCard}><strong>{preview.issuer}</strong><div>{preview.category} · {preview.public_code}</div><div>{!needsSubscription && result.data?.data?.message}</div></div>}
        {needsSubscription && <div className={styles.linkCard}><p>{scorT('opensWithSubscription')}</p><Button as={Link} to={AppRoute.scorSubscription} colorScheme="green">{scorT('getSubscription')}</Button></div>}
        {item && <ReportContent report={item} />}
      </div>}
    </section>
  </>;
};

const isRevokedShare = (share: ApiScorShare): boolean => Boolean(share.revoked_at) || share.invalid_reason?.trim().toLowerCase() === 'отозвано владельцем';

const InvitesPage: FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useFetchScorSharesQuery();
  const [revoke, revokeState] = useRevokeScorShareMutation();
  const toastError = useScorToastError();
  const items = data?.data?.items ?? [];
  const handleRevoke = async (id?: number): Promise<void> => { if (!id) return; try { await revoke({ share_id: id }).unwrap(); await refetch(); } catch (error) { toastError(error); } };

  return <>
    <SectionHeader
      title={scorT('invitations')}
      action={<Button onClick={() => navigate(AppRoute.scorShare)} isDisabled={data?.data?.limit?.can_share !== true} colorScheme="green" leftIcon={<FiSend />}>{scorT('newInvitation')}</Button>}
    />
    {!isLoading && typeof data?.data?.limit?.can_share !== 'boolean' && <div role="alert">{scorT('currentTermsUnavailable')} <Button onClick={() => { void refetch(); }}>{scorT('retry')}</Button></div>}
    <section className={`${styles.card} ${styles.invitesCard}`}>
      <QueryState isLoading={isLoading} isError={isError} error={error} isEmpty={items.length === 0}>
        <div className={styles.inviteList}>
          {items.map((share) => {
            const revoked = isRevokedShare(share);

            return <div className={styles.inviteRow} key={share.id}>
              <div className={styles.inviteHeading}>
                <div className={styles.reportName}>{share.report_issuer ?? scorT('report', { v0: share.report_id })}</div>
                <span className={styles.inviteAccess}>{share.guest_phone ?? scorT('accessViaLink')}</span>
              </div>
              <span className={`${styles.inviteStatus} ${share.is_valid ? styles.inviteStatusActive : ''}`}>
                {share.is_valid ? scorT('active') : share.invalid_reason ?? scorT('closed')}
              </span>
              <span className={styles.inviteViews}>{scorT('views')} {share.views_count ?? 0}</span>
              <span className={styles.inviteExpiry}>{scorT('until')} {formatDate(share.expires_at)}</span>
              <div className={styles.inviteAction}>
                {!revoked && share.url && <ScorCopy hideValue value={share.url} label={scorT('copyInviteLink')} appearance="inviteLink" />}
                {!revoked && share.invite_code && <ScorCopy value={share.invite_code} label={scorT('copyInviteCode')} appearance="inviteCode" />}
                {share.is_valid && <Button className={styles.inviteRevoke} size="sm" variant="ghost" isLoading={revokeState.isLoading} onClick={async () => await handleRevoke(share.id)}>{scorT('revoke')}</Button>}
              </div>
            </div>;
          })}
        </div>
      </QueryState>
    </section>
  </>;
};

const SharePage: FC = () => {
  const params = new URLSearchParams(useLocation().search);
  const reportsQuery = useFetchScorShareReportsQuery();
  const [reportId, setReportId] = useState(Number(params.get('report') ?? 0));
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');
  const [mode, setMode] = useState<ApiScorShareAccessMode>('link');
  const [createdUrl, setCreatedUrl] = useState('');
  const [createdCode, setCreatedCode] = useState('');
  const limit = reportsQuery.data?.data?.limit;
  const shareDataReady = typeof limit?.can_share === 'boolean' && !reportsQuery.isFetching && !reportsQuery.isLoading && !reportsQuery.isError;
  const selectedReport = reportsQuery.data?.data?.items?.find((item) => item.id === reportId);
  const reportLimitReached = selectedReport?.invites_left === 0;
  const canCreateInvitation = shareDataReady && Boolean(limit?.can_share) && Boolean(selectedReport) && !reportLimitReached;
  const [shareError, setShareError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [accessError, setAccessError] = useState<unknown>();
  const [shareLimitReached, setShareLimitReached] = useState(false);
  const [createShare, state] = useCreateScorShareMutation();
  const toast = useToast();
  const toastError = useScorToastError();
  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (state.isLoading || !canCreateInvitation) return;
    if (!reportId) return;
    setShareError('');
    setPhoneError('');
    setAccessError(undefined);
    let normalizedPhone: string | undefined;
    if (mode === 'phone') {
      const compact = phone.trim().replace(/[\s()-]/g, '');
      const candidate = /^[78]\d{10}$/.test(compact) ? `+7${compact.slice(1)}` : compact;
      const parsed = /^\+\d+$/.test(candidate) ? parsePhoneNumber(candidate) : undefined;
      if (!parsed?.isValid()) {
        setPhoneError(scorT(phone.trim() ? 'invalidRecipientPhone' : 'enterTheRecipientSPhoneNumber'));

        return;
      }
      normalizedPhone = parsed.number;
    }
    setShareLimitReached(false);
    try {
      const response = await createShare({ report_id: reportId, note: note || undefined, guest_phone: mode === 'phone' ? normalizedPhone : undefined, access_mode: mode }).unwrap();
      setCreatedUrl(response.data?.item?.url ?? '');
      setCreatedCode(response.data?.item?.invite_code ?? '');
    } catch (error) {
      const fieldErrors = getScorFieldErrors(error);
      setPhoneError(fieldErrors.guest_phone ?? '');
      if (getScorErrorResponse(error)?.error_code === 'SUBSCRIPTION_REQUIRED') setAccessError(error);
      const messages = Object.values(fieldErrors).filter(Boolean);
      const limitMessage = messages.find((message) => /исчерпан лимит приглашений|invitation limit|limit of invitations/i.test(message));
      if (Boolean(limitMessage) || Boolean(fieldErrors.report_id)) void reportsQuery.refetch();
      if (messages.length > 0) {
        setShareError(fieldErrors.report_id ?? limitMessage ?? '');
        setShareLimitReached(Boolean(limitMessage));
        toast({ title: fieldErrors.report_id ?? limitMessage ?? messages.join('\n'), status: limitMessage ? 'warning' : 'error', position: 'top-right' });
      } else toastError(error);
    }
  };

  return <><SectionHeader title={scorT('shareAccessToAReport')} description={scorT('createAProtectedLinkOrTieAn')} /><section className={styles.card}><p>{scorT('personalInvitations', { v0: limit?.available ?? '—', v1: limit?.total ?? '—' })}</p>{typeof limit?.per_report === 'number' && <p>{scorT('invitationsPerReport', { v0: limit.per_report })}</p>}{limit?.can_share === false && <p role="status">{scorT('noInvitationsAvailable')}</p>}<form className={styles.formGrid} onSubmit={submit}><label className={styles.fieldFull}><span className={styles.fieldLabel}>{scorT('report210')}</span><select aria-invalid={Boolean(shareError)} aria-describedby={shareError ? 'scor-share-error' : undefined} className={styles.nativeSelect} value={reportId} onChange={(event) => { setReportId(Number(event.target.value)); setShareError(''); setShareLimitReached(false); setAccessError(undefined); }}><option value={0}>{scorT('selectAReport')}</option>{reportsQuery.data?.data?.items?.map((item) => <option key={item.id} value={item.id} disabled={item.invites_left === 0}>{item.issuer}{item.is_mine === undefined ? '' : ` — ${scorT(item.is_mine ? 'ownShareReport' : 'publicShareReport')}`}{item.invites_left === 0 ? ` — ${scorT('noInvitationsAvailable')}` : typeof item.invites_left === 'number' ? ` — ${scorT('invitationsLeftForReport', { count: item.invites_left })}` : ''}</option>)}</select>{reportLimitReached && <small role="status">{scorT('noInvitationsAvailable')}</small>}{shareError && <small id="scor-share-error" className={styles.fieldError}>{shareError}</small>}</label><label><span className={styles.fieldLabel}>{scorT('accessMode')}</span><select className={styles.nativeSelect} value={mode} onChange={(event) => setMode(event.target.value as ApiScorShareAccessMode)}><option value="link">{scorT('byLink')}</option><option value="phone">{scorT('byPhone')}</option></select></label>{mode === 'phone' && <label><span className={styles.fieldLabel}>{scorT('recipientSPhoneNumber')}</span><input type="tel" autoComplete="tel" className={styles.nativeInput} aria-invalid={Boolean(phoneError)} aria-describedby={phoneError ? 'scor-phone-error' : undefined} value={phone} onChange={(event) => { setPhone(event.target.value); setPhoneError(''); }} placeholder="+7 999 000-00-00" />{phoneError && <small id="scor-phone-error" role="alert" className={styles.fieldError}>{phoneError}</small>}</label>}<label className={styles.fieldFull}><span className={styles.fieldLabel}>{scorT('message')}</span><textarea className={styles.nativeTextarea} value={note} onChange={(event) => setNote(event.target.value)} /></label><div className={styles.fieldFull}><Button type="submit" colorScheme="green" isLoading={state.isLoading} isDisabled={!canCreateInvitation} leftIcon={<FiLink />}>{scorT('createInvitation')}</Button></div>{shareLimitReached && <div className={`${styles.fieldFull} ${styles.shareLimit}`}><p role="status">{scorT('noInvitationsAvailable')}</p><Button as={Link} to={AppRoute.scorInvites} variant="outline" px="16px">{scorT('manageInvitations')}</Button></div>}{!shareDataReady && <div className={styles.fieldFull}>{reportsQuery.isFetching || reportsQuery.isLoading ? <Spinner /> : <><p>{scorT('currentTermsUnavailable')}</p>{reportsQuery.isError && <ScorError error={reportsQuery.error} />}<Button variant="outline" onClick={() => { void reportsQuery.refetch(); }}>{scorT('reload')}</Button></>}</div>}{accessError != null && <ScorError error={accessError} />}{createdUrl && <div className={`${styles.linkCard} ${styles.fieldFull}`}><ScorCopy value={createdUrl} label={scorT('copyInviteLink')} /></div>}{createdCode && <div className={styles.fieldFull}><ScorCopy value={createdCode} label={scorT('copyInviteCode')} /></div>}</form></section></>;
};

const ReferralPage: FC = () => {
  const { data, isLoading, isError, error, refetch } = useFetchScorReferralQuery();
  const referral = data?.data;
  const [code, setCode] = useState('');
  const [apply, state] = useApplyScorReferralMutation();
  const [codeError, setCodeError] = useState('');
  const [applied, setApplied] = useState(false);
  const applyingRef = useRef(false);
  const toast = useToast();
  const toastError = useScorToastError();
  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (applyingRef.current || applied) return;
    applyingRef.current = true;
    setCodeError('');
    try {
      const response = await apply({ code }).unwrap();
      setApplied(true);
      toast({ title: response.data?.message ?? scorT('codeApplied'), status: 'success', position: 'top-right' });
      void refetch();
    } catch (error) {
      const message = getScorFieldErrors(error).code;
      if (message) setCodeError(message);
      else toastError(error);
    } finally {
      applyingRef.current = false;
    }
  };

  const copyReferralCode = async (): Promise<void> => {
    if (!referral?.code) return;
    await navigator.clipboard.writeText(referral.code);
    toast({ title: scorT('invitationCodeCopied'), status: 'success', position: 'top-right' });
  };

  if (!isLoading && referral?.is_enabled === false) {
    return <><SectionHeader title={scorT('referralProgram')} description={scorT('theProgramIsCurrentlyUnavailable')} /><div className={styles.empty}>{scorT('newInvitationsAreTemporarilyUnavailable')}</div></>;
  }

  return (
    <div className={styles.stack}>
      <div>
        <div className={styles.screenKicker}>{scorT('referralProgramYourNetwork')}</div>
        <div>
          <section className={styles.referralHero}>
            <div className={styles.referralHeroCopy}>
              <div className={styles.referralEyebrow}><i />{' '}{scorT('yourSharesproNetwork')}</div>
              <h1>{scorT('anInvitationGivesPersonalAccessNotA')}</h1>
              <p>{scorT('shareSharesproWithSomeoneYouKnowThe')}</p>
              <div className={styles.referralCodeBox}>
                <span>{isLoading ? scorT('loading') : referral?.code ?? '—'}</span>
                <Button colorScheme="green" isDisabled={!referral?.code} onClick={() => { void copyReferralCode(); }}>{scorT('shareInvitation')}</Button>
              </div>
            </div>
            <div className={styles.referralHeroVisual}>
              <img src={referralImage} alt={scorT('yourSharesproNetwork')} />
              <div className={`${styles.referralChip} ${styles.referralInviteChip}`}><span>{scorT('yourNetwork')}</span><b>{formatScorInvitations(referral?.invited)}</b><em>{referral?.paid ?? '—'}{' '}{scorT('confirmed')}</em></div>
              <div className={`${styles.referralChip} ${styles.referralAccessChip}`}><span>{scorT('accessLevel')}</span><b>{scorT('personal')}</b><em>{scorT('withAnIndividualCode')}</em></div>
              <div className={styles.referralSeal}>{scorT('yourNetworkSharespro')}</div>
            </div>
          </section>
        </div>
        {isError && <ScorError error={error} />}
        <div className={styles.referralLedger}>
          <div><span>{scorT('invitations')}</span><b>{referral?.invited ?? '—'}</b></div>
          <div><span>{scorT('becameClients')}</span><b>{referral?.paid ?? '—'}</b></div>
          <div><span>{scorT('programStatus')}</span><b>{referral?.is_enabled ? scorT('active239') : scorT('unavailable')}</b></div>
        </div>
      </div>
      <div className={styles.referralDetails}>
        <section className={styles.referralRegistry}>
          <div className={styles.cardHeader}><div><h2 className={styles.sectionTitle}>{scorT('programOverview')}</h2><div className={styles.muted}>{scorT('availableStatisticsForYourNetwork')}</div></div></div>
          <div className={styles.referralRegistryRow}><span>01</span><div><b>{scorT('codeShared')}</b><small>{scorT('totalRecordedInvitations')}</small></div><em>{referral?.invited ?? '—'}</em></div>
          <div className={styles.referralRegistryRow}><span>02</span><div><b>{scorT('becameClients')}</b><small>{scorT('confirmedProgramParticipants')}</small></div><em>{referral?.paid ?? '—'}</em></div>
          <div className={styles.referralRegistryRow}><span>03</span><div><b>{scorT('programTerms')}</b><small>{scorT('yourPersonalManagerCanConfirmTheCurrent')}</small></div><em>{referral?.is_enabled ? scorT('active239') : '—'}</em></div>
        </section>
        <aside className={styles.referralRules}>
          <span className={styles.memoLabel}>{scorT('howAccessWorks')}</span>
          <h2>{scorT('aCodeCanOnlyBeAppliedOnce')}</h2>
          <p>{scorT('programTermsMayChangeYourPersonalManager')}</p>
          <div className={styles.referralSteps}><div><span>01</span><b>{scorT('shareTheCode')}</b><small>{scorT('inAnyConvenientWay')}</small></div><div><span>02</span><b>{scorT('yourFriendEntersIt')}</b><small>{scorT('duringRegistrationOrInTheirProfile')}</small></div></div>
          {referral?.can_enter_code && !applied ? <form className={styles.referralApply} onSubmit={submit}><input className={styles.nativeInput} value={code} aria-invalid={Boolean(codeError)} aria-describedby={codeError ? 'scor-referral-error' : undefined} onChange={(event) => { setCode(event.target.value); setCodeError(''); }} aria-label={scorT('invitationCode')} placeholder={scorT('enterYourInvitationCode')} />{codeError && <small id="scor-referral-error" role="alert" className={styles.fieldError}>{codeError}</small>}<Button type="submit" isLoading={state.isLoading}>{scorT('apply')}</Button></form> : <div className={styles.referralApplied}>{scorT('aReferralCodeIsAlreadyLinkedTo')}</div>}
        </aside>
      </div>
    </div>
  );
};

const PurchaseHistory: FC = () => {
  const query = useFetchScorMyPurchasesQuery(undefined, { refetchOnMountOrArgChange: true });
  const { summary, items = [] } = query.data?.data ?? {};

  return (
    <section className={`${styles.card} ${styles.invoiceListCard} ${styles.purchaseHistory}`} aria-label={scorT('purchaseHistory')}>
      <h2 className={styles.sectionTitle}>{scorT('purchaseHistory')}</h2>
      <QueryState isLoading={query.isLoading} isError={query.isError} error={query.error} isEmpty={items.length === 0} emptyText={scorT('noPurchasesYet')}>
        {summary && <div className={styles.featureList}>
          <div><b>{scorT('totalPurchased')}: {summary.total_rub == null ? '—' : new Intl.NumberFormat(getScorLocale(), { style: 'currency', currency: 'RUB', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(summary.total_rub)}</b></div>
          <div>{scorCount('purchaseMonths', summary.subscription_months)}</div>
          <div>{scorCount('purchaseReports', summary.reports_count)}</div>
          <div>{scorCount('purchaseExpress', summary.express_count)}</div>
        </div>}
        <div className={styles.invoiceList}>
          {items.map((item, index) => <article key={item.id ?? index} className={styles.invoiceListItem}>
            <div className={styles.invoiceListMain}>
              <div className={styles.eyebrow}>{item.kind_label ?? scorT('scorInvoice')}</div>
              <h3>{item.number ?? scorT('invoice', { v0: item.id ?? '—' })}</h3>
              {item.comment && <p>{item.comment}</p>}
              <div className={styles.muted}>{scorT('purchasePaidAt', { v0: formatDate(item.paid_at) })}</div>
            </div>
            <div className={styles.invoiceListAside}><strong>{formatScorInvoiceMoney(item.amount_minor)}</strong></div>
            {item.id != null && <Link className={styles.invoiceOpen} to={AppRoute.scorInvoice(item.id)}>{scorT('openInvoice')}{' '}<FiArrowRight aria-hidden="true" /></Link>}
          </article>)}
        </div>
      </QueryState>
      {query.isError && <Button variant="outline" onClick={() => { void query.refetch(); }}>{scorT('retry')}</Button>}
    </section>
  );
};

const InvoiceListItem: FC<{ invoice: ApiScorInvoice }> = ({ invoice }) => {
  const content = (
    <>
      <div className={styles.invoiceListMain}>
        <div className={styles.eyebrow}>
          {invoice.kind_label ?? scorT('scorInvoice')}
        </div>
        <h3>{invoice.number ?? scorT('invoice', { v0: invoice.id ?? '—' })}</h3>
        <div className={styles.muted}>
          {invoice.valid_until
            ? scorT('validUntil', { v0: formatDate(invoice.valid_until) })
            : scorT('noPaymentDeadlineSpecified')}
        </div>
      </div>
      <div className={styles.invoiceListAside}>
        <strong>{formatScorInvoiceMoney(invoice.amount_minor)}</strong>
        {invoice.is_online !== undefined && <small>{invoice.is_online ? scorT('onlinePayment') : scorT('bankTransfer')}</small>}
        <span className={`${styles.status} ${getInvoiceStatusClassName(invoice.status)}`}>
          {invoice.status_label ?? (invoice.is_overdue ? scorT('overdue') : scorT('awaitingPayment'))}
        </span>
      </div>
      <span className={styles.invoiceOpen}>{scorT('openInvoice')}{' '}<FiArrowRight aria-hidden="true" />
      </span>
    </>
  );

  if (!invoice.id) {
    return <article className={`${styles.invoiceListItem} ${styles.invoiceListItemDisabled}`}>{content}</article>;
  }

  return (
    <Link className={styles.invoiceListItem} to={AppRoute.scorInvoice(invoice.id)}>
      {content}
    </Link>
  );
};

const InvoiceQr: FC<{ payload: string; invoiceNumber?: string }> = ({
  payload,
  invoiceNumber,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let isCancelled = false;

    setHasError(false);
    void toCanvas(canvas, payload, {
      width: 292,
      margin: 4,
      errorCorrectionLevel: 'M',
      color: { dark: '#0b1713ff', light: '#ffffffff' },
    }).catch(() => {
      if (!isCancelled) setHasError(true);
    });

    return () => {
      isCancelled = true;
    };
  }, [payload]);

  const download = (): void => {
    const canvas = canvasRef.current;
    if (!canvas || hasError) return;
    const link = document.createElement('a');
    const safeNumber = (invoiceNumber ?? 'scor').replace(/[^a-zA-Z0-9_-]/g, '-');
    link.download = `${safeNumber}-qr.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  return (
    <div className={styles.invoiceQrCard}>
      <div>
        <div className={styles.eyebrow}>{scorT('payUsingYourBankSApp')}</div>
        <h2 className={styles.sectionTitle}>{scorT('scanTheQrCode')}</h2>
        <p className={styles.muted}>{scorT('yourBankWillFillInTheAmount')}</p>
      </div>
      {hasError
        ? (
          <div className={styles.invoiceQrError} role="status">
            <FiAlertCircle aria-hidden="true" />{scorT('unableToGenerateAQrCodeUse')}</div>
          )
        : <canvas ref={canvasRef} className={styles.invoiceQrCanvas} aria-label={scorT('invoicePaymentQrCode')} />}
      <Button
        type="button"
        variant="outline"
        leftIcon={<FiDownload />}
        isDisabled={hasError}
        onClick={download}
      >{scorT('saveQrCode')}</Button>
    </div>
  );
};

const getInvoiceRequisites = (): Array<{
  key: keyof ApiScorPaymentRequisites;
  label: string;
}> => [
  { key: 'name', label: scorT('recipient') },
  { key: 'inn', label: scorT('taxIdInn') },
  { key: 'kpp', label: scorT('taxRegistrationCodeKpp') },
  { key: 'account', label: scorT('accountNumber') },
  { key: 'bank_name', label: scorT('bank') },
  { key: 'bic', label: scorT('bankIdentificationCodeBik') },
  { key: 'corr_account', label: scorT('correspondentAccount') },
];

const InvoicePage: FC = () => {
  const { pathname, state } = useLocation();
  const invoiceId = getNumberParam(getLastPathSegment(pathname));
  const dispatch = useAppDispatch();
  const toast = useToast();
  const toastError = useScorToastError();
  const [pollingInterval, setPollingInterval] = useState(7000);
  const [payerName, setPayerName] = useState('');
  const [payerEmail, setPayerEmail] = useState('');
  const [payerNameError, setPayerNameError] = useState('');
  const [payerEmailError, setPayerEmailError] = useState('');
  const handledFinalInvoice = useRef<number>();
  const orderPrice: ApiScorOrderPrice | undefined = state?.invoiceId === invoiceId ? state.orderPrice : undefined;
  const [savePayer, saveState] = useSaveScorPayerMutation();
  const [createPayment, paymentState] = useCreateScorPaymentMutation();
  const paymentBusy = useRef(false);
  const [paymentError, setPaymentError] = useState('');
  const query = useFetchScorInvoiceQuery(
    { id: invoiceId },
    {
      skip: invoiceId <= 0,
      pollingInterval,
      refetchOnFocus: true,
      refetchOnMountOrArgChange: true,
    }
  );
  const invoice = query.data?.data?.item;
  const payment = query.data?.data?.payment;
  const isFinal = isScorInvoiceFinalStatus(invoice?.status);
  const isPaid = invoice?.status === 2;
  const isOnline = invoice?.is_online === true;
  const canPay = !isFinal && invoice?.can_pay !== false;
  const canPayOnline = canPay && isOnline && (invoice?.status === 1 || invoice?.status === 3);
  const payerIsRequired = (invoice?.payer_required?.length ?? 0) > 0;
  const visibleRequisites = getInvoiceRequisites().filter(({ key }) =>
    Boolean(payment?.requisites?.[key]?.trim())
  );
  const isNotFound = isSharesproFetchQueryError(query.error) && ((query.error.data as SharesproApiResponse<unknown>).error_code === 'NOT_FOUND' || query.error.status === 404);

  useEffect(() => {
    setPollingInterval(7000);
    handledFinalInvoice.current = undefined;
  }, [invoiceId]);

  useEffect(() => {
    const updatePolling = (): void => {
      setPollingInterval(
        isFinal || document.hidden || !document.hasFocus() ? 0 : 7000
      );
    };
    updatePolling();
    document.addEventListener('visibilitychange', updatePolling);
    const refreshOnFocus = (): void => { updatePolling(); void query.refetch(); };
    window.addEventListener('focus', refreshOnFocus);
    window.addEventListener('blur', updatePolling);

    return () => {
      document.removeEventListener('visibilitychange', updatePolling);
      window.removeEventListener('focus', refreshOnFocus);
      window.removeEventListener('blur', updatePolling);
    };
  }, [isFinal]);

  useEffect(() => {
    if (
      isFinal &&
      invoice?.id &&
      handledFinalInvoice.current !== invoice.id
    ) {
      handledFinalInvoice.current = invoice.id;
      dispatch(
        scorApi.util.invalidateTags([
          'ScorInvoices',
          'ScorShowcase',
          'ScorSubscription',
          'ScorOrders',
        ])
      );
    }
  }, [dispatch, invoice?.id, isFinal]);

  useEffect(() => {
    setPayerName(invoice?.payer_name ?? '');
    setPayerEmail(invoice?.payer_email ?? '');
  }, [invoice?.id, invoice?.payer_email, invoice?.payer_name]);

  const copyValue = async (label: string, value?: string | null): Promise<void> => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      toast({
        title: scorT('copied', { v0: label }),
        status: 'success',
        position: 'top-right',
      });
    } catch (_error) {
      toast({
        title: scorT('unableToCopyTheValue'),
        status: 'error',
        position: 'top-right',
      });
    }
  };

  const openPayment = async (payableInvoice: ApiScorInvoice): Promise<void> => {
    if (payableInvoice.can_pay === false || !payableInvoice.id || !payableInvoice.is_online || ![1, 3].includes(payableInvoice.status ?? 0) || payableInvoice.payer_required?.length) return;
    setPaymentError('');
    const response = await createPayment({ invoice_id: payableInvoice.id }).unwrap();
    if (!response.data?.payment_url) {
      setPaymentError(scorT('paymentLinkUnavailable'));

      return;
    }
    window.location.assign(response.data.payment_url);
  };

  const payInvoice = async (): Promise<void> => {
    if (paymentBusy.current || !invoice || !canPayOnline || payerIsRequired) return;
    paymentBusy.current = true;
    try {
      await openPayment(invoice);
    } catch (error) {
      toastError(error);
      void query.refetch();
    } finally {
      paymentBusy.current = false;
    }
  };

  const submitPayer = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    const normalizedName = payerName.trim().replace(/\s+/g, ' ');
    const normalizedEmail = payerEmail.trim();
    const nextNameError = normalizedName.split(' ').filter(Boolean).length < 2
      ? scorT('enterYourFirstAndLastNameAt')
      : '';
    const nextEmailError = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)
      ? ''
      : scorT('enterAValidEmailAddressForThe');
    setPayerNameError(nextNameError);
    setPayerEmailError(nextEmailError);
    if (!canPay || nextNameError || nextEmailError || !invoice?.id || paymentBusy.current) return;
    paymentBusy.current = true;

    try {
      await savePayer({
        invoice_id: invoice.id,
        payer_name: normalizedName,
        payer_email: normalizedEmail,
      }).unwrap();
      const refreshed = await query.refetch().unwrap();
      const refreshedInvoice = refreshed.data?.item;

      if (refreshedInvoice?.can_pay === false) return;
      if (refreshedInvoice?.is_online === true) {
        await openPayment(refreshedInvoice);

        return;
      }

      toast({
        title: scorT('payerDetailsSaved'),
        description: refreshedInvoice?.is_online === false ? scorT('youCanNowPayUsingTheQr') : scorT('refreshTheInvoiceToCheckThePayment'),
        status: 'success',
        position: 'top-right',
      });
    } catch (error) {
      const errors = getScorFieldErrors(error);
      setPayerNameError(errors.payer_name ?? '');
      setPayerEmailError(errors.payer_email ?? '');
      toastError(error);
    } finally {
      paymentBusy.current = false;
    }
  };

  if (invoiceId <= 0 || isNotFound) {
    return (
      <>
        <SectionHeader title={scorT('invoiceNotFound')} description={scorT('theInvoiceWasNotFoundOrIs')} />
        <section className={`${styles.card} ${styles.invoiceStateCard}`}>
          <FiAlertCircle aria-hidden="true" />
          <p>{scorT('checkTheLinkOrReturnToYour')}</p>
          <Button as={Link} to={AppRoute.scorSubscription} variant="outline">{scorT('backToInvoices')}</Button>
        </section>
      </>
    );
  }

  if (query.isLoading && !invoice) {
    return (
      <>
        <SectionHeader title={scorT('scorInvoice')} description={scorT('loadingCurrentPaymentDetails')} />
        <section className={`${styles.card} ${styles.invoiceStateCard}`}>
          <Spinner color="primary.600" />
        </section>
      </>
    );
  }

  if (query.isError && !invoice) {
    return (
      <>
        <SectionHeader title={scorT('scorInvoice')} description={scorT('unableToLoadPaymentDetails')} />
        <section className={`${styles.card} ${styles.invoiceStateCard}`}>
          <FiAlertCircle aria-hidden="true" />
          <ScorError error={query.error} />
          <Button variant="outline" onClick={() => { void query.refetch(); }}>{scorT('retry')}</Button>
        </section>
      </>
    );
  }

  if (!invoice) return null;

  return (
    <div className={styles.invoicePage}>
      <SectionHeader
        eyebrow={invoice.kind_label ?? scorT('scorPayment')}
        title={invoice.number ?? scorT('invoice', { v0: invoice.id ?? '—' })}
        description={scorT('checkTheAmountAndChooseAnAvailable')}
        action={<Button as={Link} to={AppRoute.scorSubscription} variant="outline">{scorT('backToInvoices')}</Button>}
      />

      {query.isError && (
        <div className={styles.invoiceRefreshNotice} role="status">{scorT('unableToRefreshTheStatusTheLast')}<button type="button" onClick={() => { void query.refetch(); }}>{scorT('retry')}</button>
        </div>
      )}

      <section className={styles.invoiceSummaryCard}>
        <div className={styles.invoiceSummaryTop}>
          <div>
            <span className={styles.invoiceCaption}>{scorT('amountDue299')}</span>
            <div className={styles.invoiceAmount}>
              {formatScorInvoiceMoney(invoice.amount_minor)}
            </div>
          </div>
          <span
            className={`${styles.invoiceStatus} ${getInvoiceStatusClassName(invoice.status)}`}
            aria-live="polite"
          >
            {invoice.status_label ?? scorT('invoiceStatus')}
          </span>
        </div>
        <div className={styles.invoiceMetaGrid}>
          <div><span>{scorT('invoiceType')}</span><b>{invoice.kind_label ?? '—'}</b></div>
          <div><span>{scorT('issued')}</span><b>{formatDate(invoice.issued_at)}</b></div>
          <div><span>{scorT('payBy')}</span><b>{formatDate(invoice.valid_until)}</b></div>
          <div><span>{scorT('order')}</span><b>{invoice.order_id ? `№ ${invoice.order_id}` : scorT('notLinked')}</b></div>
        </div>
        {invoice.comment && <p className={styles.invoiceComment}>{invoice.comment}</p>}
      </section>

      {orderPrice && <section className={styles.card}>
        <h2 className={styles.sectionTitle}>{scorT('orderPriceDetails')}</h2>
        {orderPrice.source && <p>{scorT(orderPrice.source === 1 ? 'orderPriceIncluded' : orderPrice.source === 2 ? 'orderPriceExtra' : 'orderPriceSingle')}</p>}
        <dl className={styles.invoiceRequisites}>
          {orderPrice.price != null && <div><dt>{scorT('orderPriceBase')}</dt><dd>{formatMoney(orderPrice.price)}</dd></div>}
          {orderPrice.discount != null && <div><dt>{scorT('orderPriceDiscount')}</dt><dd>{orderPrice.discount}</dd></div>}
          {orderPrice.total != null && <div><dt>{scorT('orderPriceTotal')}</dt><dd>{formatMoney(orderPrice.total)}</dd></div>}
        </dl>
      </section>}

      {(invoice.status === 3 || invoice.is_overdue) && (
        <div className={styles.invoiceWarning} role="status">
          <FiAlertCircle aria-hidden="true" />
          <div><b>{scorT('paymentDeadlinePassed')}</b><span>{scorT('youCanStillPayThisInvoiceIts')}</span></div>
        </div>
      )}

      {isPaid && (
        <section className={styles.invoiceResultCard} role="status">
          <span><FiCheck aria-hidden="true" /></span>
          <div><h2>{scorT('paymentReceived')}</h2><p>{scorT('theInvoiceStatusHasBeenUpdatedNo')}</p></div>
        </section>
      )}

      {(invoice.status === 8 || invoice.status === 9) && (
        <section className={styles.invoiceResultCard} role="status">
          <div><h2>{invoice.status_label ?? scorT('invoiceClosed')}</h2><p>{scorT('thisInvoiceCanNoLongerBePaid')}</p></div>
        </section>
      )}

      {payment?.note && (
        <div className={styles.invoiceNote} role="note">
          <FiAlertCircle aria-hidden="true" />
          <span>{payment.note}</span>
        </div>
      )}

      {canPay && payerIsRequired && (
        <section className={`${styles.card} ${styles.invoicePayerCard}`}>
          <div className={styles.invoiceSectionHeading}>
            <div className={styles.eyebrow}>{scorT('receiptDetails')}</div>
            <h2 className={styles.sectionTitle}>{scorT('enterPayerDetails')}</h2>
            <p className={styles.muted}>{scorT('thePayerSNameWillAppearIn')}</p>
          </div>
          <form className={styles.invoicePayerForm} onSubmit={submitPayer} noValidate>
            <label>
              <span className={styles.fieldLabel}>{scorT('firstAndLastName')}</span>
              <input
                className={`${styles.nativeInput} ${payerNameError ? styles.nativeInputError : ''}`}
                value={payerName}
                autoComplete="name"
                aria-invalid={Boolean(payerNameError)}
                onChange={(event) => { setPayerName(event.target.value); setPayerNameError(''); }}
              />
              {payerNameError && <span className={styles.fieldError}>{payerNameError}</span>}
            </label>
            <label>
              <span className={styles.fieldLabel}>{scorT('receiptEmail')}</span>
              <input
                className={`${styles.nativeInput} ${payerEmailError ? styles.nativeInputError : ''}`}
                type="email"
                value={payerEmail}
                autoComplete="email"
                aria-invalid={Boolean(payerEmailError)}
                onChange={(event) => { setPayerEmail(event.target.value); setPayerEmailError(''); }}
              />
              {payerEmailError && <span className={styles.fieldError}>{payerEmailError}</span>}
            </label>
            <Button className={styles.invoicePayerSubmit} type="submit" colorScheme="green" isLoading={saveState.isLoading || paymentState.isLoading}>
              {isOnline ? scorT('saveAndProceedToPayment') : invoice.is_online === false ? scorT('saveAndShowBankDetails') : scorT('saveDetails')}
            </Button>
          </form>
        </section>
      )}

      {canPay && invoice.is_online === undefined && <div role="alert" className={styles.invoicePaymentUnavailable}>{scorT('thePaymentMethodIsNotYetAvailable')}{' '}<button type="button" onClick={() => { void query.refetch(); }}>{scorT('refreshInvoice')}</button></div>}
      {canPayOnline && !payerIsRequired && (
        <section className={styles.invoiceOnlineCard}>
          <div>
            <div className={styles.eyebrow}>{scorT('onlinePayment')}</div>
            <h2 className={styles.sectionTitle}>{scorT('payOnTheSecurePage')}</h2>
            <p className={styles.muted}>{scorT('theLinkWillOpenOnThisDevice')}</p>
          </div>
          <Button onClick={() => { void payInvoice(); }} isLoading={paymentState.isLoading} isDisabled={saveState.isLoading} colorScheme="green" rightIcon={<FiExternalLink />}>{scorT('payNow')}</Button>
          {paymentError && <p role="alert">{paymentError}</p>}
        </section>
      )}

      {canPay && !payerIsRequired && invoice.is_online === false && (
        <div className={styles.invoicePaymentGrid}>
          {invoice.qr_payload && <InvoiceQr payload={invoice.qr_payload} invoiceNumber={invoice.number} />}
          <section className={styles.invoiceDetailsCard}>
            <div className={styles.invoiceSectionHeading}>
              <div className={styles.eyebrow}>{scorT('bankTransfer')}</div>
              <h2 className={styles.sectionTitle}>{scorT('paymentDetails')}</h2>
            </div>
            {visibleRequisites.length > 0 && (
              <dl className={styles.invoiceRequisites}>
                {visibleRequisites.map(({ key, label }) => {
                  const value = payment?.requisites?.[key];

                  return (
                    <div key={key}>
                      <dt>{label}</dt>
                      <dd>{value}</dd>
                      <button type="button" aria-label={scorT('copy327', { v0: label })} onClick={() => { void copyValue(label, value); }}>
                        <FiCopy aria-hidden="true" />
                      </button>
                    </div>
                  );
                })}
              </dl>
            )}
            {payment?.details && <div className={styles.paymentDetails}>{payment.details}</div>}
            {visibleRequisites.length === 0 && !payment?.details && (
              <div className={styles.invoicePaymentUnavailable}>{scorT('bankDetailsHaveNotBeenPublishedYet')}</div>
            )}
            {invoice.purpose && (
              <div className={styles.invoicePurpose}>
                <div><span>{scorT('paymentReference')}</span><p>{invoice.purpose}</p></div>
                <Button type="button" variant="outline" leftIcon={<FiCopy />} onClick={() => { void copyValue(scorT('reference'), invoice.purpose); }}>{scorT('copy')}</Button>
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
};

const SubscriptionPage: FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, isFetching, isError, error, refetch, fulfilledTimeStamp } = useFetchScorSubscriptionQuery(undefined, { refetchOnMountOrArgChange: true });
  const documentsQuery = useFetchScorDocumentsQuery(undefined, { refetchOnMountOrArgChange: true });
  const documentsReady = !documentsQuery.isFetching && !documentsQuery.isError && typeof documentsQuery.data?.data?.accepted_required === 'boolean';
  const hasAcceptedDocuments = documentsQuery.data?.data?.accepted_required === true;
  const [legalDocument, setLegalDocument] = useState<ApiScorLegalDocument>();
  const invoicesQuery = useFetchScorMyInvoicesQuery();
  const { data: profile } = useFetchProfileQuery();
  const subscription = data?.data?.subscription;
  const pricing = data?.data?.pricing;
  const saleEnabled = isSubscriptionSaleEnabled(data?.data);
  const showSubscription = saleEnabled || subscription?.is_active === true;
  const [requestSubscription, requestState] = useCreateScorSubscriptionMutation();
  const requestBlockedRef = useRef(false);
  const [savedConfirmation] = useState(readSubscriptionConfirmation);
  const [submittedHere, setSubmittedHere] = useState(false);
  const savedAt = useRef(savedConfirmation?.savedAt ?? 0);
  const [requestNotice, setRequestNotice] = useState<string | null>(savedConfirmation ? savedConfirmation.data.message ?? scorT('aManagerWillContactYouShortly') : null);
  const [confirmation, setConfirmation] = useState<ApiCreateScorSubscriptionResponseData | undefined>(savedConfirmation?.data);
  const [consent, setConsent] = useState(false);
  useEffect(() => { setConsent(false); }, [documentsQuery.fulfilledTimeStamp, documentsQuery.data?.data?.accepted_required]);
  const [selection, setSelection] = useState<{ planId?: number; periodId?: number }>();
  const plans = pricing?.plans?.items ?? [];
  const selectedPlan = plans.find((plan) => plan.id != null && plan.id === selection?.planId);
  const selectedPeriod = selectedPlan?.periods?.find((period) => period.id != null && period.id === selection?.periodId);
  const hasValidSelection = Boolean(selectedPlan && selectedPeriod && typeof selectedPeriod.price === 'number' && Number.isFinite(selectedPeriod.price));
  const replacementPlan = selectedPlan ?? plans.find((plan) => plan.is_default) ?? plans[0];
  const pending = data?.data?.has_pending_request;
  const pendingInvoice = getPendingInvoice(data?.data);

  useEffect(() => {
    if (selection !== undefined || !pricing?.plans?.items?.length) return;
    const plan = pricing.plans.items.find((item) => item.is_default) ?? pricing.plans.items[0];
    setSelection({ planId: plan.id, periodId: plan.periods?.[0]?.id });
  }, [pricing, selection]);

  useEffect(() => {
    // A newly fetched server state supersedes the immediate submission notice.
    if (!isLoading && !isFetching && !isError && (Boolean(pendingInvoice) || pending === false || subscription?.is_active) && (savedAt.current === 0 || (fulfilledTimeStamp ?? 0) > savedAt.current)) {
      clearSubscriptionConfirmation();
      savedAt.current = 0;
      setRequestNotice(null);
      setConfirmation(undefined);
      setSubmittedHere(false);
      requestBlockedRef.current = false;
    }
  }, [pendingInvoice, pending, fulfilledTimeStamp, isLoading, isFetching, isError, subscription?.is_active]);
  const [requestErrors, setRequestErrors] = useState<Record<string, string>>({});
  const [contactName, setContactName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const toast = useToast();
  const toastError = useScorToastError();

  useEffect(() => {
    if (!profile) return;
    const fullName = [profile.lastName, profile.firstName, profile.secondName]
      .filter(Boolean)
      .join(' ');
    setContactName((current) => current.length > 0 ? current : fullName);
    setPhone((current) => current.length > 0 ? current : profile.phone ?? '');
    setEmail((current) => current.length > 0 ? current : profile.email ?? '');
  }, [profile]);

  const submitRequest = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (!saleEnabled || !documentsReady || Boolean(pendingInvoice) || requestBlockedRef.current || requestState.isLoading || requestNotice !== null || isFetching || isError || data?.data?.has_pending_request !== false) return;
    const errors: Record<string, string> = {};
    if (!phone.trim()) errors.contact_phone = scorT('enterYourPhoneNumber');
    if (!hasAcceptedDocuments && !consent) errors.consent = scorT('acceptanceOfTheOfferIsRequired');
    if (!hasValidSelection) errors.period_id = scorT('selectAnAvailablePlanAndBillingPeriod');
    setRequestErrors(errors);
    if (Object.keys(errors).length > 0) return;
    requestBlockedRef.current = true;
    try {
      const response = await requestSubscription({
        contact_name: contactName.trim() || undefined,
        contact_phone: phone.trim(),
        contact_email: email.trim() || undefined,
        consent: hasAcceptedDocuments || consent,
        plan_id: selectedPlan?.id,
        period_id: selectedPeriod?.id,
      }).unwrap();
      if (response.data?.invoice?.id) {
        clearSubscriptionConfirmation();
        navigate(AppRoute.scorInvoice(response.data.invoice.id));

        return;
      }
      savedAt.current = saveSubscriptionConfirmation(response.data ?? {});
      setSubmittedHere(true);
      setConfirmation(response.data ?? {});
      setRequestNotice(response.data?.message ?? scorT('aManagerWillContactYouShortly'));
      toast({
        title: response.data?.message ?? scorT('requestSubmitted'),
        status: 'success',
        position: 'top-right',
      });
      await refetch();
    } catch (error) {
      const duplicateMessage = Object.values(getScorFieldErrors(error))
        .find((message) => /^(Заявка уже отправлена|Request already submitted|A request has already been submitted)/i.test(message.trim()));
      if (duplicateMessage) {
        setSubmittedHere(true);
        setRequestNotice(duplicateMessage);
        toast({ title: duplicateMessage, status: 'warning', position: 'top-right' });
        void refetch();
      } else {
        requestBlockedRef.current = false;
        setRequestErrors(getScorFieldErrors(error));
        toastError(error);
      }
    }
  };
  const invoices = invoicesQuery.data?.data?.items ?? [];

  return (
    <>
      <SectionHeader title={showSubscription ? scorUiText(data?.data?.ui?.scor_subscription?.title, 'scorSubscription') : scorT('pricesAndPurchases')} description={showSubscription ? scorUiText(data?.data?.ui?.scor_subscription?.subtitle, 'fullAccessToReportsRecordedOutcomesAnd') : undefined} />
      <QueryState isLoading={(isLoading || isFetching) && !submittedHere} isError={isError && !submittedHere} error={error}>
        <div className={styles.stack}>
          {saleEnabled && pendingInvoice && <section className={styles.card}><PendingInvoice invoice={pendingInvoice} /></section>}
          {showSubscription && <div className={subscription?.is_active ? styles.twoColumns : styles.stack}>
            {subscription?.is_active && <section className={`${styles.card} ${styles.illustrationCard}`}>
              <div>
                <div className={styles.eyebrow}>{scorT('subscriptionActive')}</div>
                <h2 className={styles.sectionTitle}>{subscription?.price_month == null ? '—' : formatMoney(subscription.price_month)}{' '}{scorT('perMonth')}</h2>
                <div className={styles.featureList}>
                  <div className={styles.feature}><span className={styles.featureIcon}><FiCheck /></span>{formatScorIncludedReports(subscription?.reports_limit)}</div>
                  <div className={styles.feature}><span className={styles.featureIcon}><FiCheck /></span>{scorT('discountOnAdditionalReports')}{' '}{subscription?.extra_discount ?? '—'}%</div>
                  <div className={styles.feature}><span className={styles.featureIcon}><FiCheck /></span>{scorT('upTo')}{' '}{subscription?.share_limit ?? '—'}{' '}{scorT('protectedInvitations')}</div>
                </div>
              </div>
              <img src={subscriptionImage} alt={scorT('scorSubscription')} />
            </section>}
            {(Boolean(subscription?.is_active) || !pendingInvoice) && <section className={styles.card}>
              {subscription?.is_active
                ? (
                  <>
                    <h2 className={styles.sectionTitle}>{scorT('yourAccess')}</h2>
                    <div className={styles.metricValue}>{formatScorDaysLeft(subscription.days_left)}</div>
                    <div className={styles.muted}>{scorT('paidUntil')}{' '}{formatDate(subscription.paid_until)}</div>
                    <div className={styles.featureList}>
                      <div>{scorT('reportsUsed')}{' '}{subscription.reports_used ?? 0}</div>
                      <div>{scorT('reportsRemaining')}{' '}{subscription.reports_left ?? 0}</div>
                    </div>
                  </>
                  )
                : requestNotice !== null || data?.data?.has_pending_request === true ? (
                  <div role="status">
                    <h2 className={styles.sectionTitle}>{scorT('requestSubmitted')}</h2>
                    <p className={styles.muted}>{requestNotice ?? scorT('aManagerWillContactYouShortly')}</p>
                    {confirmation && <div data-plan-period-id={confirmation.plan_period_id ?? undefined}>
                      {confirmation.period_label && <p>{scorT('period')}{' '}{confirmation.period_label}</p>}
                      {confirmation.amount != null && <p>{scorT('requestAmount')}{' '}{formatMoney(confirmation.amount)}</p>}
                    </div>}
                  </div>
                  ) : (
                  isFetching ? <Spinner color="primary.600" /> : typeof data?.data?.has_pending_request !== 'boolean' ? (
                    <div role="alert"><p>{scorT('unableToLoadYourRequestStatus')}</p><Button variant="outline" onClick={() => { void refetch(); }}>{scorT('reload')}</Button></div>
                  ) : <form noValidate className={styles.subscriptionRequestForm} onSubmit={submitRequest}>
                    <h2 className={styles.sectionTitle}>{scorT('subscribe')}</h2>
                    <p className={styles.muted}>{scorT('checkYourContactPhoneNumberNameAnd')}</p>
                    <div className={styles.subscriptionChoices}>
                      {pricing?.plans?.show_plan_choice ? <label><span className={styles.fieldLabel}>{scorT('plan')}</span>
                        <select className={styles.nativeSelect} value={selectedPlan?.id ?? ''} disabled={requestState.isLoading} aria-invalid={Boolean(requestErrors.plan_id)} onChange={(event) => {
                          const plan = plans.find((item) => item.id === Number(event.target.value));
                          setSelection({ planId: plan?.id, periodId: plan?.periods?.[0]?.id });
                          setRequestErrors((current) => ({ ...current, plan_id: '', period_id: '' }));
                        }}><option value="" disabled>{scorT('selectAPlan')}</option>{plans.filter((plan) => plan.id != null).map((plan) => <option key={plan.id} value={plan.id}>{plan.name}</option>)}</select>
                      </label> : selectedPlan && <b>{selectedPlan.name}</b>}
                      {selectedPlan?.description && <p className={styles.muted}>{selectedPlan.description}</p>}
                      {pricing?.plans?.show_period_choice ? <label><span className={styles.fieldLabel}>{scorT('billingPeriod')}</span>
                        <select className={styles.nativeSelect} value={selectedPeriod?.id ?? ''} disabled={requestState.isLoading} aria-invalid={Boolean(requestErrors.period_id)} onChange={(event) => {
                          setSelection({ planId: selectedPlan?.id, periodId: Number(event.target.value) });
                          setRequestErrors((current) => ({ ...current, period_id: '' }));
                        }}><option value="" disabled>{scorT('selectAPeriod')}</option>{selectedPlan?.periods?.filter((period) => period.id != null).map((period) => <option key={period.id} value={period.id}>{period.label}</option>)}</select>
                      </label> : selectedPeriod && <span>{selectedPeriod.label}</span>}
                      {selectedPeriod && <div><b>{formatMoney(selectedPeriod.price)}{' '}{scorT('perPeriod')}</b>{selectedPeriod.price_per_month != null && <p className={styles.muted}>{formatMoney(selectedPeriod.price_per_month)}{' '}{scorT('perMonth')}</p>}</div>}
                      {requestErrors.plan_id && <small role="alert" className={styles.fieldError}>{requestErrors.plan_id}</small>}
                      {requestErrors.period_id && <small role="alert" className={styles.fieldError}>{requestErrors.period_id}</small>}
                      {!hasValidSelection && <div role="alert">{scorT('selectAnAvailablePlanAndBillingPeriod353')}{' '}<button type="button" onClick={() => { void refetch(); }}>{scorT('reloadPlans')}</button>
                        {replacementPlan?.id != null && replacementPlan.periods?.[0]?.id != null && <Button type="button" variant="outline" mt="8px" onClick={() => {
                          setSelection({ planId: replacementPlan.id, periodId: replacementPlan.periods?.[0]?.id });
                          setRequestErrors((current) => ({ ...current, plan_id: '', period_id: '' }));
                        }}>{scorT('select')}{' '}{replacementPlan.name} · {replacementPlan.periods[0].label}</Button>}
                      </div>}
                    </div>
                    <label><span className={styles.fieldLabel}>{scorT('yourName')}</span><input className={styles.nativeInput} value={contactName} autoComplete="name" aria-invalid={Boolean(requestErrors.contact_name)} onChange={(event) => { setContactName(event.target.value); setRequestErrors((current) => ({ ...current, contact_name: '' })); }} />{requestErrors.contact_name && <small className={styles.fieldError}>{requestErrors.contact_name}</small>}</label>
                    <label><span className={styles.fieldLabel}>{scorT('phone')}</span><input required type="tel" className={styles.nativeInput} value={phone} autoComplete="tel" aria-invalid={Boolean(requestErrors.contact_phone)} onChange={(event) => { setPhone(event.target.value); setRequestErrors((current) => ({ ...current, contact_phone: '' })); }} />{requestErrors.contact_phone && <small className={styles.fieldError}>{requestErrors.contact_phone}</small>}</label>
                    <label><span className={styles.fieldLabel}>Email</span><input className={styles.nativeInput} type="email" value={email} autoComplete="email" aria-invalid={Boolean(requestErrors.contact_email)} onChange={(event) => { setEmail(event.target.value); setRequestErrors((current) => ({ ...current, contact_email: '' })); }} />{requestErrors.contact_email && <small className={styles.fieldError}>{requestErrors.contact_email}</small>}</label>
                    {!documentsReady ? <div role="alert">{scorT('currentTermsUnavailable')} <Button onClick={() => { void documentsQuery.refetch(); }}>{scorT('retry')}</Button></div> : hasAcceptedDocuments ? <p>{scorT('youHaveAlreadyAcceptedTheCurrentDocument')}</p> : <label className={styles.orderCheck}><input type="checkbox" required checked={consent} aria-invalid={Boolean(requestErrors.consent)} onChange={(event) => { setConsent(event.target.checked); setRequestErrors((current) => ({ ...current, consent: '' })); }} /><span>{scorT('iAcceptTheOffer')}{requestErrors.consent && <small role="alert" className={styles.fieldError}>{requestErrors.consent}</small>}</span></label>}
                    {documentsQuery.data?.data?.items?.filter((item) => item.type === 1).map((item) => <Button key={item.id} variant="link" isDisabled={!hasLegalDocumentContent(item)} onClick={() => setLegalDocument(item)}>{item.title}</Button>)}
                    <Button type="submit" colorScheme="green" isDisabled={!hasValidSelection || !documentsReady} isLoading={requestState.isLoading}>{scorUiText(data?.data?.payment?.cta, 'payNow')}</Button>
                    {data?.data?.payment?.hint && <p className={styles.muted}>{data.data.payment.hint}</p>}
                  </form>
                  )}
            </section>}
          </div>}
          {saleEnabled && subscription?.is_active && plans.length > 0 && (
            <section className={styles.card}>
              <h2 className={styles.sectionTitle}>{scorT('plans')}</h2>
              <div className={styles.planGrid}>
                {plans.map((plan) => (
                  <article key={plan.id ?? plan.code} className={styles.planCard}>
                    <b>{plan.name}</b>
                    {plan.description && <span>{plan.description}</span>}
                    {plan.periods?.map((period) => (
                      <div key={period.id ?? period.billing_period}><span>{period.label}</span><b>{formatMoney(period.price)}</b></div>
                    ))}
                  </article>
                ))}
              </div>
            </section>
          )}
          <PricingDocument document={pricing?.document} />
          <LegalDocumentDialog item={legalDocument} onClose={() => setLegalDocument(undefined)} />
          {(invoicesQuery.isLoading || invoices.length > 0 || invoicesQuery.isError) && (
            <section className={`${styles.card} ${styles.invoiceListCard}`}>
              <div className={styles.cardHeader}>
                <div><div className={styles.eyebrow}>{scorT('payment')}</div><h2 className={styles.sectionTitle}>{scorT('invoicesAwaitingPayment')}</h2></div>
              </div>
              <QueryState
                isLoading={invoicesQuery.isLoading}
                isError={invoicesQuery.isError} error={invoicesQuery.error}
                isEmpty={!invoicesQuery.isLoading && invoices.length === 0}
              >
                <div className={styles.invoiceList}>
                  {invoices.map((invoice) => <InvoiceListItem key={invoice.id} invoice={invoice} />)}
                </div>
              </QueryState>
            </section>
          )}
        </div>
      </QueryState>
      <PurchaseHistory />
    </>
  );
};

const FaqPage: FC = () => {
  const dispatch = useAppDispatch();
  const { data, isLoading, isError, error } = useFetchScorFaqQuery();
  const pricing = useFetchScorPricingQuery();
  const chats = useFetchChatListQuery({ page: 1 });
  const [markViewed] = useMarkScorFaqViewedMutation();
  const markOpened = (element: HTMLDetailsElement, id?: number): void => {
    if (element.open && id != null) void markViewed({ id });
  };
  const items = data?.data?.items ?? [];
  const sections = data?.data?.sections ?? {};
  const groupedItems = Object.entries(sections).map(([code, title]) => ({
    code,
    title,
    items: items.filter((item) => item.section === code),
  }));
  const knownSections = new Set(Object.keys(sections));
  const ungroupedItems = items.filter((item) => !item.section || !knownSections.has(item.section));
  const supportChat = chats.data?.chats.items.find((chat) => chat.type === ChatRoomType.CommunityManager);

  const openSupportChat = (): void => {
    if (!supportChat) return;
    dispatch(widgetChatSlice.actions.open({
      id: supportChat.id,
      type: supportChat.type,
      name: supportChat.name ?? scorT('support'),
    }));
  };

  return <><SectionHeader title={scorT('questionsAndAnswers')} description={scorT('aboutScorMethodologySubscriptionsAndPrivacy')} /><section className={styles.card}><QueryState isLoading={isLoading} isError={isError} error={error} isEmpty={items.length === 0}><div className={styles.faqSections}>{groupedItems.filter((group) => group.items.length > 0).map((group) => <section key={group.code}><h2 className={styles.sectionTitle}>{group.title}</h2>{group.items.map((item) => <details className={styles.faqItem} key={item.id} onToggle={(event) => markOpened(event.currentTarget, item.id)}><summary>{item.question}</summary><p>{formatScorFaqAnswer(item.answer ?? '', pricing.data?.data)}</p></details>)}</section>)}{ungroupedItems.length > 0 && <section>{ungroupedItems.map((item) => <details className={styles.faqItem} key={item.id} onToggle={(event) => markOpened(event.currentTarget, item.id)}><summary>{item.question}</summary><p>{formatScorFaqAnswer(item.answer ?? '', pricing.data?.data)}</p></details>)}</section>}</div></QueryState><div className={styles.faqSupport}><div><b>{scorT('stillHaveAQuestion')}</b><span>{scorT('messageTheSharesproSupportChat')}</span></div><Button variant="outline" leftIcon={<FiMessageCircle />} isDisabled={!supportChat} onClick={openSupportChat}>{scorT('openChat')}</Button></div></section></>;
};

const DocumentsPage: FC = () => {
  const { data, isLoading, isError, error } = useFetchScorDocumentsQuery();
  const [legalDocument, setLegalDocument] = useState<ApiScorLegalDocument>();
  const items = data?.data?.items ?? [];

  return (
    <div>
      <div className={styles.screenKicker}>{scorT('scorRulesAndSecurity')}</div>
      <section className={styles.docsHero}>
        <div className={styles.docsHeroCopy}>
          <div className={styles.eyebrow}>{scorT('legalDocuments')}</div>
          <h1>{scorT('scorDocuments')}</h1>
          <p>{scorT('serviceTermsConsentsAndCurrentDocumentVersions')}</p>
          <div className={styles.docsSecurity}>
            <span>{scorT('currentVersions')}</span>
            <span>{scorT('consentTracking')}</span>
            <span>{scorT('protectedAccess')}</span>
          </div>
        </div>
        <div className={styles.docsVisual}><img src={documentsImage} alt={scorT('protectedScorDocumentStorage')} /></div>
      </section>

      {isError && <ScorError error={error} />}
      {isLoading && <div className={styles.empty}><Spinner color="primary.600" /></div>}
      {!isLoading && !isError && items.length === 0 && <div className={styles.empty}>{scorT('documentsHaveNotBeenPublishedYet')}</div>}
      <div className={styles.documentGrid}>
        {items.map((document) => (
          <article className={styles.documentCard} key={document.id}>
            <div className={styles.documentCover}><i /><i /><i /><span>SCOR</span></div>
            <div className={styles.documentCopy}>
              {hasLegalDocumentContent(document) ? <button type="button" className={styles.documentTitle} onClick={() => setLegalDocument(document)}>{document.title}</button> : <div className={styles.documentTitle}>{document.title}</div>}
              <div className={styles.muted}>{scorT('version')}{' '}{document.version ?? '—'}{' '}{scorT('effectiveFrom')}{' '}{formatDate(document.valid_from)}</div>
              <div className={styles.documentActions}>
                {(Boolean(document.url) || Boolean(document.body)) && <Button size="sm" variant="ghost" onClick={() => setLegalDocument(document)}>{scorT('open385')}</Button>}
              </div>
            </div>
          </article>
        ))}
      </div>
      <LegalDocumentDialog item={legalDocument && (items.find((item) => item.id === legalDocument.id) ?? legalDocument)} onClose={() => setLegalDocument(undefined)} />
    </div>
  );
};

const ScorContent: FC = () => {
  const { pathname } = useLocation();
  if (pathname === AppRoute.scorOrder) return <OrderPage />;
  if (pathname === AppRoute.scorReports) return <ReportsPage />;
  if (pathname.match(/^\/scor\/reports\/\d+$/)) return <ReportDetail />;
  if (pathname === AppRoute.scorPool) return <PoolPage />;
  if (pathname.match(/^\/scor\/pool\/\d+$/)) return <ReportDetail pool />;
  if (pathname === AppRoute.scorShared) return <SharedPage />;
  if (pathname.startsWith(`${AppRoute.scorShared}/`)) return <SharedViewPage />;
  if (pathname === AppRoute.scorFind) return <FindPage />;
  if (pathname === AppRoute.scorInvites) return <InvitesPage />;
  if (pathname === AppRoute.scorShare) return <SharePage />;
  if (pathname === AppRoute.scorReferral) return <ReferralPage />;
  if (pathname === AppRoute.scorInvoices) return <ScorInvoicesPage />;
  if (pathname.match(/^\/scor\/invoices\/\d+$/)) return <InvoicePage />;
  if (pathname === AppRoute.scorSubscription) return <SubscriptionPage />;
  if (pathname === AppRoute.scorFaq) return <FaqPage />;
  if (pathname === AppRoute.scorDocuments) return <DocumentsPage />;

  return <Overview />;
};

const ScorPage: FC = () => {
  useTranslation();
  const { pathname } = useLocation();
  useEffect(() => {
    document.title = 'SCOR · SharesPro';
  }, []);

  return (
    <PageLayout scrollResetKey={pathname} stableScrollArea responsive>
      <Container>
        <div className={styles.page}>
          <ScorSidebar />
          <main className={styles.content}><ScorContent /></main>
        </div>
      </Container>
    </PageLayout>
  );
};

export default ScorPage;
